import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import RichTextEditor from '../../components/ui/RichTextEditor';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useUserAccess } from '../../context/UserAccessContext';
import { useHubupCommunities } from '../../context/HubupCommunitiesContext';
import {
  fetchHubupGroupMessages,
  fetchHubupGroupTopics,
  fetchHubupGroup,
  fetchHubupGroupScheduledNotifications,
  patchHubupGroupFeedBrand,
  fetchHubupGroupCatchallSender,
  patchHubupGroupCatchallSender,
  createHubupTopicMessage,
  createHubupNotificationAllTopics,
  updateHubupMessage,
  deleteHubupMessage,
  resendHubupMessagePush,
  patchHubupGroupScheduledNotification,
  deleteHubupGroupScheduledNotification,
  uploadHubupNotificationImage,
  resolveHubupAssetUrl,
  buildHubupNotificationUrl,
  fetchHubupNotificationDrafts,
  createHubupNotificationDraft,
  updateHubupNotificationDraft,
  deleteHubupNotificationDraft,
  publishHubupNotificationDraft,
  fetchHubupGroupFormAnnuaire,
  fetchHubupMessageForm,
  fetchHubupMessageCalendarEvent,
  importHubupNotificationMediaFromUrls,
} from '../../api/hubup';
import NotificationFormEditor from '../../components/hubup/NotificationFormEditor';
import NotificationCalendarEventEditor from '../../components/hubup/NotificationCalendarEventEditor';
import GenerateNotificationBrowserModal from '../../components/hubup/GenerateNotificationBrowserModal';
import HubupFormAnnuaireRecipientModal from '../../components/hubup/HubupFormAnnuaireRecipientModal';
import HubupAiSuggestedMediaReview from '../../components/hubup/HubupAiSuggestedMediaReview';
import HubupNotificationLivePreviewAside from '../../components/hubup/HubupNotificationLivePreviewAside';
import HubupNotificationSocialShareModal from '../../components/hubup/HubupNotificationSocialShareModal';
import HexColorField from '../../components/hubup/HexColorField';
import { HUBUP_FEED_BRAND_FONTS } from '../../constants/hubupFeedBrandFonts';
import { normalizeHexColor } from '../../utils/hubupHexColor';
import {
  consumeHubupGenerateDraftNotice,
  deferHubupGenerateOpenForm,
  runHubupGenerateNotificationJob,
  subscribeHubupGenerateJob,
} from '../../lib/hubupGenerateNotificationJob';
import {
  emptyNotificationForm,
  notificationFormFromApi,
  validateNotificationFormDraft,
  resolveNotificationFormPayload,
} from '../../utils/hubupNotificationForm';
import {
  emptyNotificationCalendarEvent,
  calendarEventFromApi,
  validateCalendarEventDraft,
  resolveNotificationCalendarEventPayload,
  minCalendarDateInputParis,
  formatCalendarEventPreviewLine,
} from '../../utils/hubupNotificationCalendarEvent';

const SUBJECT_NOTIFY_EVERYONE = '__notify_everyone__';

function isHubupEditorTopic(t) {
  return String(t?.topicKind || '').trim() === 'editor';
}

const BODY_PREVIEW_LEN = 160;

function stripHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function messageBodyHasContent(html) {
  const raw = String(html || '');
  if (/<img\b/i.test(raw)) return true;
  return stripHtml(raw).length > 0;
}

function rewriteBodyHtmlImageSrcForEditor(html) {
  const raw = String(html || '');
  if (!/<img\b/i.test(raw)) return raw;
  return raw.replace(
    /(<img\b[^>]*\ssrc\s*=\s*)(["'])([^"']+)\2/gi,
    (_m, pre, q, src) => `${pre}${q}${resolveHubupAssetUrl(src)}${q}`,
  );
}

function normalizeGenerateUrlInput(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

function messagePlainPreview(m) {
  const b = (m.body || '').trim();
  if (b) return b;
  return stripHtml(m.bodyHtml || '');
}

function extractTargetVisitorIds(row) {
  const raw = row?.targetVisitorIds ?? row?.visitorIds;
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x).trim()).filter(Boolean);
}

function isOneToOneNotification(row) {
  return extractTargetVisitorIds(row).length > 0;
}

function oneToOneRecipientCount(row) {
  return extractTargetVisitorIds(row).length;
}

function HubupOneToOneBadge({ row }) {
  if (!isOneToOneNotification(row)) return null;
  const n = oneToOneRecipientCount(row);
  return (
    <span
      className="text-[10px] font-semibold uppercase text-violet-800 bg-violet-100 dark:bg-violet-950/50 dark:text-violet-200 px-2 py-0.5 rounded-full"
      title={
        n === 1
          ? 'Envoyée à une seule personne'
          : `Envoyée à ${n} personnes précises`
      }
    >
      One to One{n > 1 ? ` · ${n}` : ''}
    </span>
  );
}

function messageBodyEditorInitialHtml(m) {
  const h = (m.bodyHtml || '').trim();
  if (h) return rewriteBodyHtmlImageSrcForEditor(h);
  const plain = (m.body || '').trim();
  if (!plain) return '<p></p>';
  const esc = plain
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${esc.replace(/\n\n+/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
}

function formatParisDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function minScheduleDateInputParis() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
}

function parseHubupApiError(err, fallback = 'Enregistrement impossible.') {
  const raw = String(err?.data?.message || err?.message || '').trim();
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error.trim();
    if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message.trim();
  } catch {
    /* plain text */
  }
  return raw;
}

function scheduledSuccessHint(data) {
  if (!data?.scheduled) return null;
  const when = data.scheduledForParis || formatParisDate(data.scheduledFor);
  return when
    ? `Envoi planifié le ${when} (heure de Paris). Aucun push ni message dans le fil avant cette heure.`
    : 'Envoi planifié (heure de Paris). Aucun push ni message dans le fil avant cette heure.';
}

function scheduleRowTargetLabel(s) {
  if (s.allTopics) return 'Toutes les rubriques';
  if (Array.isArray(s.topicsHint) && s.topicsHint.length > 0) {
    return s.topicsHint.map((t) => t.title).join(', ');
  }
  if (Array.isArray(s.topicIds) && s.topicIds.length > 0) {
    return `${s.topicIds.length} rubrique(s)`;
  }
  return '—';
}

const NOTIF_IMAGES_MAX = 5;
const NOTIF_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
const NOTIF_VIDEO_MAX_BYTES = 50 * 1024 * 1024;

const NOTIF_IMAGE_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
]);

const NOTIF_VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

const NOTIF_PDF_MIME_TYPES = new Set(['application/pdf']);

const NOTIF_MEDIA_ACCEPT =
  'image/png,image/jpeg,image/jpg,image/gif,image/webp,video/mp4,video/webm,video/quicktime,application/pdf,.mp4,.webm,.mov,.pdf';

const NOTIF_MEDIA_TYPE_HINT =
  'images PNG/JPEG/WebP/GIF, vidéos MP4/WebM/MOV ou PDF';

function isNotifVideoFile(file) {
  const t = String(file?.type || '')
    .trim()
    .toLowerCase();
  if (t && NOTIF_VIDEO_MIME_TYPES.has(t)) return true;
  return /\.(mp4|webm|mov)$/i.test(String(file?.name || ''));
}

function isNotifVideoPath(path) {
  const t = String(path || '').trim().toLowerCase();
  if (!t) return false;
  if (t.includes('/notification-videos/')) return true;
  return /\.(mp4|webm|mov)(\?|$)/i.test(t);
}

function isNotifPdfFile(file) {
  const t = String(file?.type || '')
    .trim()
    .toLowerCase();
  if (t && NOTIF_PDF_MIME_TYPES.has(t)) return true;
  return /\.pdf$/i.test(String(file?.name || ''));
}

function isNotifPdfPath(path) {
  const t = String(path || '').trim().toLowerCase();
  if (!t) return false;
  if (t.includes('/notification-pdfs/')) return true;
  return /\.pdf(\?|$)/i.test(t);
}

function notifPdfDisplayName(path, file) {
  if (file?.name) return String(file.name);
  const raw = String(path || '').split('/').pop() || '';
  const m = raw.match(/^notif-pdf-\d+-[a-f0-9]+-(.+)\.pdf$/i);
  if (m?.[1]) return `${m[1].replace(/-/g, ' ')}.pdf`;
  return raw || 'document.pdf';
}

function isNotifLargeMediaFile(file) {
  return isNotifVideoFile(file) || isNotifPdfFile(file);
}

function isAllowedNotifMediaFile(file) {
  if (isNotifLargeMediaFile(file)) return true;
  const t = String(file?.type || '')
    .trim()
    .toLowerCase();
  if (t && NOTIF_IMAGE_MIME_TYPES.has(t)) return true;
  return /\.(png|jpe?g|gif|webp)$/i.test(String(file?.name || ''));
}

function notifMediaMaxBytes(file) {
  return isNotifLargeMediaFile(file)
    ? NOTIF_VIDEO_MAX_BYTES
    : NOTIF_IMAGE_MAX_BYTES;
}

function notifMediaMaxLabel(file) {
  return isNotifLargeMediaFile(file) ? '50 Mo' : '3 Mo';
}

function NotifIllustrationPreview({ src, path, file, className }) {
  const isVideo = file ? isNotifVideoFile(file) : isNotifVideoPath(path);
  const isPdf = file ? isNotifPdfFile(file) : isNotifPdfPath(path);
  if (isVideo) {
    return (
      <video
        src={src}
        className={className}
        muted
        playsInline
        preload="metadata"
      />
    );
  }
  if (isPdf) {
    return (
      <div
        className={`${className} flex flex-col items-center justify-center gap-0.5 bg-red-50 text-red-700 dark:bg-red-950/35 dark:text-red-300`}
        title={path || file?.name ? notifPdfDisplayName(path, file) : 'PDF'}
      >
        <Icon name="FileType2" size={26} strokeWidth={1.75} />
        <span className="text-[9px] font-bold uppercase tracking-wider leading-none">
          PDF
        </span>
      </div>
    );
  }
  return <img src={src} alt="" className={className} />;
}

function revokePendingObjectUrls(items) {
  for (const p of items) {
    try {
      URL.revokeObjectURL(p.objectUrl);
    } catch {}
  }
}

function normalizeFormImageUrls(arr) {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  const out = [];
  for (const x of arr) {
    const s = String(x).trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= NOTIF_IMAGES_MAX) break;
  }
  return out;
}

const NOTIF_BODY_OR_MEDIA_MSG =
  'Ajoutez un texte dans le message, validez un formulaire, ou joignez au moins un média (illustrations).';

function notificationHasGalleryOrPendingMedia(imageUrls, pendingMediaCount = 0) {
  if (pendingMediaCount > 0) return true;
  return normalizeFormImageUrls(imageUrls || []).length > 0;
}

/** Corps sans texte autorisé si formulaire validé ou médias illustrations (titre requis à part). */
function notificationBodyRequirementMet({
  bodyHtml,
  formEnabled,
  formValidated,
  eventEnabled,
  eventValidated,
  imageUrls,
  pendingMediaCount = 0,
}) {
  if (messageBodyHasContent(bodyHtml)) return true;
  if (formEnabled && formValidated) return true;
  if (eventEnabled && eventValidated) return true;
  if (notificationHasGalleryOrPendingMedia(imageUrls, pendingMediaCount)) return true;
  return false;
}

function messageImageUrlsFromModel(m) {
  if (Array.isArray(m?.imageUrls) && m.imageUrls.length > 0) {
    return normalizeFormImageUrls(m.imageUrls);
  }
  const one = (m?.imageUrl || '').trim();
  return one ? [one] : [];
}

function scheduleImageUrlsFromModel(s) {
  if (Array.isArray(s?.imageUrls) && s.imageUrls.length > 0) {
    return normalizeFormImageUrls(s.imageUrls);
  }
  const one = (s?.imageUrl || '').trim();
  return one ? [one] : [];
}

const emptyForm = () => ({
  topicId: '',
  title: '',
  bodyHtml: '<p></p>',
  linkUrl: '',
  imageUrls: [],
  priority: 'normal',
});

const HubupNotifications = () => {
  const { groupId } = useParams();
  const { user } = useUserAccess();
  const { communities, refetch: refetchCommunities } = useHubupCommunities();
  const isAdmin = user?.permission === 'admin';
  const canManageHubup = isAdmin || user?.hubup === true;
  const currentCommunity = useMemo(
    () => communities.find((c) => c.id === groupId),
    [communities, groupId],
  );
  const notifRemaining = useMemo(() => {
    if (isAdmin) return null;
    return Math.max(0, Number(currentCommunity?.notificationLeft) || 0);
  }, [isAdmin, currentCommunity?.notificationLeft]);
  const quotaExhausted = notifRemaining !== null && notifRemaining <= 0;
  const canBuyPacks = !isAdmin && user?.hubup === true;
  const QUOTA_MSG =
    'Cette communauté n\'a plus de notifications disponibles. Rechargez via « Voir les offres ».';

  const openOffersModal = () => {
    window.dispatchEvent(new CustomEvent('hubup:open-offers'));
  };

  const [generateBrowserOpen, setGenerateBrowserOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateMediaReview, setGenerateMediaReview] = useState(null);
  const [generateMediaImportLoading, setGenerateMediaImportLoading] =
    useState(false);
  const [generateMediaImportError, setGenerateMediaImportError] = useState(null);
  const [draftAiMediaReview, setDraftAiMediaReview] = useState(null);
  const pageMountedRef = useRef(true);

  useEffect(() => {
    pageMountedRef.current = true;
    return () => {
      pageMountedRef.current = false;
      deferHubupGenerateOpenForm();
    };
  }, []);

  useEffect(() => {
    if (!groupId) return undefined;
    const pendingId = consumeHubupGenerateDraftNotice(groupId);
    if (pendingId) {
      setHint(
        'Votre brouillon est prêt dans la liste des brouillons.',
      );
      const t = setTimeout(() => setHint(''), 8000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [groupId]);

  useEffect(() => {
    return subscribeHubupGenerateJob((snap) => {
      if (snap.groupId === groupId) {
        setGenerateLoading(snap.inFlight);
      }
    });
  }, [groupId]);

  const [messages, setMessages] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hint, setHint] = useState('');
  const [publicLinkModal, setPublicLinkModal] = useState(null);
  const [socialShareMessage, setSocialShareMessage] = useState(null);
  const [publicLinkCopyHint, setPublicLinkCopyHint] = useState('');
  const [expandedBody, setExpandedBody] = useState(() => new Set());

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const formRef = useRef(form);
  formRef.current = form;
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingImageItems, setPendingImageItems] = useState([]);
  const pendingFilesRef = useRef([]);
  pendingFilesRef.current = pendingImageItems;
  const [resendId, setResendId] = useState(null);
  const [scheduleSend, setScheduleSend] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduledItems, setScheduledItems] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editReschedule, setEditReschedule] = useState(false);
  const [notifyPush, setNotifyPush] = useState(true);
  const [isPrivateNotification, setIsPrivateNotification] = useState(false);
  const [annuaireContacts, setAnnuaireContacts] = useState([]);
  const [limitToAnnuaire, setLimitToAnnuaire] = useState(false);
  const [targetVisitorIds, setTargetVisitorIds] = useState([]);
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [hadTargetVisitorsOnOpen, setHadTargetVisitorsOnOpen] = useState(false);

  const [isDuplicateDraft, setIsDuplicateDraft] = useState(false);

  const [drafts, setDrafts] = useState([]);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const [formPanel, setFormPanel] = useState('notification');
  const [linkedForm, setLinkedForm] = useState(null);
  const [formEnabled, setFormEnabled] = useState(false);
  const [formValidated, setFormValidated] = useState(false);
  const [hadFormInitially, setHadFormInitially] = useState(false);
  const [loadingLinkedForm, setLoadingLinkedForm] = useState(false);

  const [linkedEvent, setLinkedEvent] = useState(null);
  const [eventEnabled, setEventEnabled] = useState(false);
  const [eventValidated, setEventValidated] = useState(false);
  const [hadEventInitially, setHadEventInitially] = useState(false);
  const [loadingLinkedEvent, setLoadingLinkedEvent] = useState(false);

  const [joinWelcomeConfigured, setJoinWelcomeConfigured] = useState(false);
  const [groupMetaLoaded, setGroupMetaLoaded] = useState(false);

  const [feedBrandOpen, setFeedBrandOpen] = useState(false);
  const [feedFont, setFeedFont] = useState('');
  const [feedColorHexBg, setFeedColorHexBg] = useState('');
  const [feedColorHexFont, setFeedColorHexFont] = useState('');
  const [feedBrandSaving, setFeedBrandSaving] = useState(false);
  const [feedBrandError, setFeedBrandError] = useState(null);
  const [feedBrandHint, setFeedBrandHint] = useState('');

  const [catchallAllowed, setCatchallAllowed] = useState(false);
  const [catchallOpen, setCatchallOpen] = useState(false);
  const [catchallSenderEmail, setCatchallSenderEmail] = useState('');
  const [catchallSaving, setCatchallSaving] = useState(false);
  const [catchallError, setCatchallError] = useState(null);
  const [catchallHint, setCatchallHint] = useState('');

  const clearPendingImages = () => {
    revokePendingObjectUrls(pendingFilesRef.current);
    setPendingImageItems([]);
  };

  const resetLinkedFormState = () => {
    setFormPanel('notification');
    setLinkedForm(null);
    setFormEnabled(false);
    setFormValidated(false);
    setHadFormInitially(false);
    setLoadingLinkedForm(false);
    setLinkedEvent(null);
    setEventEnabled(false);
    setEventValidated(false);
    setHadEventInitially(false);
    setLoadingLinkedEvent(false);
  };

  const applyLinkedFormFromApi = (raw, hadBefore = false) => {
    const parsed = notificationFormFromApi(raw);
    if (parsed) {
      setLinkedForm(parsed);
      setFormEnabled(true);
      setFormValidated(true);
      setHadFormInitially(hadBefore);
    } else {
      setLinkedForm(null);
      setFormEnabled(false);
      setFormValidated(false);
      setHadFormInitially(hadBefore);
    }
  };

  const openFormEditor = () => {
    if (!linkedForm) {
      setLinkedForm(emptyNotificationForm());
    }
    setFormPanel('form');
    setFormError(null);
  };

  const finishFormEditor = () => {
    const err = validateNotificationFormDraft(linkedForm);
    if (err) {
      setFormError(err);
      return;
    }
    setFormEnabled(true);
    setFormValidated(true);
    setFormPanel('notification');
    setFormError(null);
  };

  const removeLinkedForm = () => {
    setLinkedForm(null);
    setFormEnabled(false);
    setFormValidated(false);
    setFormPanel('notification');
    setFormError(null);
  };

  const resolveFormPayloadForSave = () => {
    if (formEnabled && linkedForm) {
      if (!formValidated) {
        return {
          error:
            'Validez le formulaire avant d’enregistrer (bouton « Valider le formulaire »).',
        };
      }
      const err = validateNotificationFormDraft(linkedForm);
      if (err) return { error: err };
      return resolveNotificationFormPayload(true, linkedForm, hadFormInitially);
    }
    return resolveNotificationFormPayload(false, null, hadFormInitially);
  };

  const applyLinkedEventFromApi = (raw, hadBefore = false) => {
    const parsed = calendarEventFromApi(raw);
    if (parsed) {
      setLinkedEvent(parsed);
      setEventEnabled(true);
      setEventValidated(true);
      setHadEventInitially(hadBefore);
    } else {
      setLinkedEvent(null);
      setEventEnabled(false);
      setEventValidated(false);
      setHadEventInitially(hadBefore);
    }
  };

  const openEventEditor = () => {
    if (!linkedEvent) {
      setLinkedEvent({
        ...emptyNotificationCalendarEvent(),
        startDate: minCalendarDateInputParis(),
      });
    }
    setFormPanel('event');
    setFormError(null);
  };

  const finishEventEditor = () => {
    const err = validateCalendarEventDraft(linkedEvent);
    if (err) {
      setFormError(err);
      return;
    }
    setEventEnabled(true);
    setEventValidated(true);
    setFormPanel('notification');
    setFormError(null);
  };

  const removeLinkedEvent = () => {
    setLinkedEvent(null);
    setEventEnabled(false);
    setEventValidated(false);
    setFormPanel('notification');
    setFormError(null);
  };

  const resolveEventPayloadForSave = () => {
    if (eventEnabled && linkedEvent) {
      if (!eventValidated) {
        return {
          error:
            'Validez l’événement calendrier avant d’enregistrer (bouton « Valider l’événement »).',
        };
      }
      const err = validateCalendarEventDraft(linkedEvent);
      if (err) return { error: err };
      return resolveNotificationCalendarEventPayload(
        true,
        linkedEvent,
        hadEventInitially,
      );
    }
    return resolveNotificationCalendarEventPayload(
      false,
      null,
      hadEventInitially,
    );
  };

  const resolveExtrasPayloadForSave = () => {
    const formResult = resolveFormPayloadForSave();
    if (formResult.error) return formResult;
    const eventResult = resolveEventPayloadForSave();
    if (eventResult.error) return eventResult;
    return { payload: { ...formResult.payload, ...eventResult.payload } };
  };

  const loadMessageLinkedForm = async (messageId, hadFormHint = false) => {
    setLoadingLinkedForm(true);
    try {
      const res = await fetchHubupMessageForm(messageId);
      applyLinkedFormFromApi(res?.data?.form, hadFormHint || !!res?.data?.form);
    } catch {
      applyLinkedFormFromApi(null, hadFormHint);
    } finally {
      setLoadingLinkedForm(false);
    }
  };

  const loadMessageLinkedEvent = async (messageId, hadEventHint = false) => {
    setLoadingLinkedEvent(true);
    try {
      const res = await fetchHubupMessageCalendarEvent(messageId);
      applyLinkedEventFromApi(res?.data?.calendarEvent, hadEventHint || !!res?.data?.calendarEvent);
    } catch {
      applyLinkedEventFromApi(null, hadEventHint);
    } finally {
      setLoadingLinkedEvent(false);
    }
  };

  const topicsSelectable = useMemo(
    () => topics.filter((t) => isAdmin || !isHubupEditorTopic(t)),
    [topics, isAdmin],
  );

  const hasUsableTopicsForCommunityPublish = useMemo(
    () => (isAdmin ? topics.length > 0 : topicsSelectable.length > 0),
    [isAdmin, topics.length, topicsSelectable.length],
  );

  const messagesListed = useMemo(() => {
    if (isAdmin) return messages;
    return messages.filter((m) => {
      const t = topics.find((x) => x.id === m.topicId);
      if (!t) return true;
      return !isHubupEditorTopic(t);
    });
  }, [messages, topics, isAdmin]);

  const notificationPreviewFeedName = useMemo(() => {
    if (isAdmin && form.topicId === SUBJECT_NOTIFY_EVERYONE) {
      return 'HubUP';
    }
    return (
      currentCommunity?.name?.trim() ||
      topics.find((t) => t.id === form.topicId)?.title ||
      'Communauté'
    );
  }, [isAdmin, form.topicId, currentCommunity?.name, topics]);

  const notificationPreviewMedia = useMemo(() => {
    const items = [];
    for (const path of form.imageUrls || []) {
      const src = resolveHubupAssetUrl(path);
      if (!src) continue;
      if (isNotifPdfPath(path)) {
        items.push({
          key: path,
          kind: 'pdf',
          label: notifPdfDisplayName(path),
          src,
        });
      } else if (isNotifVideoPath(path)) {
        items.push({ key: path, kind: 'video', src });
      } else {
        items.push({ key: path, kind: 'image', src });
      }
    }
    for (const p of pendingImageItems) {
      const src = p.objectUrl;
      if (!src) continue;
      if (isNotifPdfFile(p.file)) {
        items.push({
          key: p.id,
          kind: 'pdf',
          label: notifPdfDisplayName(null, p.file),
          src,
        });
      } else if (isNotifVideoFile(p.file)) {
        items.push({ key: p.id, kind: 'video', src });
      } else {
        items.push({ key: p.id, kind: 'image', src });
      }
    }
    return items;
  }, [form.imageUrls, pendingImageItems]);

  const notificationPreviewHasForm = useMemo(() => {
    if (!linkedForm) return false;
    if (formPanel === 'form') {
      const q = linkedForm.questions;
      return Boolean(
        String(linkedForm.name || '').trim() ||
          (Array.isArray(q) && q.length > 0),
      );
    }
    return formEnabled && formValidated;
  }, [formPanel, linkedForm, formEnabled, formValidated]);

  const notificationPreviewCard = useMemo(
    () => ({
      feedName: notificationPreviewFeedName,
      groupIsPro: currentCommunity?.isPro === true,
      groupIconUrl: currentCommunity?.groupIconUrl,
      title: form.title,
      bodyHtml: form.bodyHtml,
      linkUrl: form.linkUrl,
      mediaItems: notificationPreviewMedia,
      priority: form.priority,
      hasForm: notificationPreviewHasForm,
      groupFeedFont: feedFont,
      groupColorHexBg: feedColorHexBg,
      groupColorHexFont: feedColorHexFont,
    }),
    [
      notificationPreviewFeedName,
      currentCommunity?.isPro,
      currentCommunity?.groupIconUrl,
      form.title,
      form.bodyHtml,
      form.linkUrl,
      form.priority,
      notificationPreviewMedia,
      notificationPreviewHasForm,
      feedFont,
      feedColorHexBg,
      feedColorHexFont,
    ],
  );

  const scheduledItemsListed = useMemo(() => {
    if (isAdmin) return scheduledItems;
    return scheduledItems.filter((s) => {
      if (s.allTopics) return true;
      const ids = Array.isArray(s.topicIds) ? s.topicIds : [];
      if (ids.length === 0) return true;
      const onlyEditor = ids.every((id) => {
        const t = topics.find((x) => x.id === id);
        return t ? isHubupEditorTopic(t) : false;
      });
      return !onlyEditor;
    });
  }, [scheduledItems, topics, isAdmin]);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const msgP = fetchHubupGroupMessages(groupId);
      const topP = fetchHubupGroupTopics(groupId);
      const schedP = canManageHubup
        ? fetchHubupGroupScheduledNotifications(groupId)
        : Promise.resolve({ data: { items: [] } });
      const grpP = fetchHubupGroup(groupId).catch(() => null);
      const [msgRes, topRes, schedRes, grpRes] = await Promise.all([
        msgP,
        topP,
        schedP,
        grpP,
      ]);
      setMessages(msgRes?.data?.messages || []);
      setTopics(topRes?.data?.topics || []);
      setScheduledItems(schedRes?.data?.items || []);
      setJoinWelcomeConfigured(grpRes?.data?.joinWelcomeConfigured === true);
      const grp = grpRes?.data?.group;
      if (grp) {
        setFeedFont(String(grp.font ?? '').trim());
        setFeedColorHexBg(String(grp.colorHexBg ?? '').trim());
        setFeedColorHexFont(String(grp.colorHexFont ?? '').trim());
      }
      if (canManageHubup && groupId) {
        try {
          const dr = await fetchHubupNotificationDrafts(groupId);
          setDrafts(dr?.data?.drafts || []);
        } catch {
          setDrafts([]);
        }
      } else {
        setDrafts([]);
      }
      setGroupMetaLoaded(true);
    } catch (e) {
      setError('Impossible de charger les notifications.');
      setMessages([]);
      setTopics([]);
      setScheduledItems([]);
      setJoinWelcomeConfigured(false);
      setGroupMetaLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [groupId, canManageHubup]);

  const feedBrandStatusLabel = useMemo(() => {
    if (String(feedFont || '').trim()) return 'Style actif';
    return 'Par défaut HubUP';
  }, [feedFont]);

  const feedFontOptions = useMemo(() => {
    const current = String(feedFont ?? '').trim();
    if (current && !HUBUP_FEED_BRAND_FONTS.some((o) => o.value === current)) {
      return [...HUBUP_FEED_BRAND_FONTS, { value: current, label: current }];
    }
    return HUBUP_FEED_BRAND_FONTS;
  }, [feedFont]);

  const toggleFeedBrandPanel = () => {
    setFeedBrandOpen((open) => {
      if (open) setFeedBrandError(null);
      return !open;
    });
  };

  useEffect(() => {
    if (!canManageHubup || !groupId) return;
    let cancelled = false;
    void fetchHubupGroupCatchallSender(groupId)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data ?? {};
        setCatchallAllowed(d.allowed === true);
        setCatchallSenderEmail(String(d.senderEmail ?? '').trim());
      })
      .catch(() => {
        if (!cancelled) setCatchallAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canManageHubup, groupId]);

  useEffect(() => {
    if (!groupId) {
      setAnnuaireContacts([]);
      return undefined;
    }
    let cancelled = false;
    void fetchHubupGroupFormAnnuaire(groupId)
      .then((res) => {
        if (cancelled) return;
        setAnnuaireContacts(
          Array.isArray(res?.data?.items) ? res.data.items : [],
        );
      })
      .catch(() => {
        if (!cancelled) setAnnuaireContacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const showAnnuaireRecipientPicker =
    !isPrivateNotification &&
    (annuaireContacts.length > 0 ||
      limitToAnnuaire ||
      targetVisitorIds.length > 0 ||
      hadTargetVisitorsOnOpen);

  const resetAnnuaireRecipientState = () => {
    setLimitToAnnuaire(false);
    setTargetVisitorIds([]);
    setRecipientModalOpen(false);
    setHadTargetVisitorsOnOpen(false);
  };

  const applyTargetVisitorsFromRow = (row) => {
    const ids = extractTargetVisitorIds(row);
    setTargetVisitorIds(ids);
    setLimitToAnnuaire(ids.length > 0);
    setHadTargetVisitorsOnOpen(ids.length > 0);
    setRecipientModalOpen(false);
  };

  const mergeTargetVisitorIds = (payload) => {
    if (limitToAnnuaire && targetVisitorIds.length > 0) {
      return { ...payload, targetVisitorIds };
    }
    if (
      (editing || editingSchedule) &&
      hadTargetVisitorsOnOpen &&
      !limitToAnnuaire
    ) {
      return { ...payload, targetVisitorIds: [] };
    }
    return payload;
  };

  const catchallStatusLabel = useMemo(() => {
    if (String(catchallSenderEmail || '').trim()) return 'E-mail renseigné';
    return 'Non configuré';
  }, [catchallSenderEmail]);

  const toggleCatchallPanel = () => {
    setCatchallOpen((open) => {
      if (open) setCatchallError(null);
      return !open;
    });
  };

  const handleSaveCatchallSender = async () => {
    if (catchallSaving || !groupId) return;
    setCatchallSaving(true);
    setCatchallError(null);
    setCatchallHint('');
    try {
      const res = await patchHubupGroupCatchallSender(
        groupId,
        String(catchallSenderEmail ?? '').trim(),
      );
      const saved = String(res?.data?.senderEmail ?? '').trim();
      setCatchallSenderEmail(saved);
      setCatchallHint('E-mail expéditeur enregistré pour cette communauté.');
    } catch (e) {
      setCatchallError(
        e?.data?.message ||
          e?.message ||
          'Impossible d’enregistrer l’e-mail expéditeur.',
      );
    } finally {
      setCatchallSaving(false);
    }
  };

  const handleSaveFeedBrand = async () => {
    if (!groupId || feedBrandSaving) return;
    const bg = normalizeHexColor(feedColorHexBg);
    const fg = normalizeHexColor(feedColorHexFont);
    if (feedColorHexBg.trim() && !bg) {
      setFeedBrandError('Couleur de fond invalide (hex attendu, ex. #010064).');
      return;
    }
    if (feedColorHexFont.trim() && !fg) {
      setFeedBrandError('Couleur de texte invalide (hex attendu, ex. #333483).');
      return;
    }
    setFeedBrandSaving(true);
    setFeedBrandError(null);
    setFeedBrandHint('');
    try {
      const res = await patchHubupGroupFeedBrand(groupId, {
        font: String(feedFont ?? '').trim(),
        colorHexBg: bg,
        colorHexFont: fg,
      });
      const g = res?.data?.group;
      if (g) {
        setFeedFont(String(g.font ?? '').trim());
        setFeedColorHexBg(String(g.colorHexBg ?? '').trim());
        setFeedColorHexFont(String(g.colorHexFont ?? '').trim());
      }
      setFeedBrandHint('Style des annonces enregistré.');
      setTimeout(() => setFeedBrandHint(''), 4000);
    } catch (e) {
      setFeedBrandError(
        e?.message || 'Impossible d’enregistrer le style des annonces.',
      );
    } finally {
      setFeedBrandSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      revokePendingObjectUrls(pendingFilesRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showForm) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showForm]);

  const uploadPendingAndMergeUrls = async (existingImageUrls) => {
    const pending = pendingFilesRef.current;
    const base = normalizeFormImageUrls(existingImageUrls);
    if (!pending.length) return base;

    const room = Math.max(0, NOTIF_IMAGES_MAX - base.length);
    const batch = pending.slice(0, room);

    if (!batch.length) {
      revokePendingObjectUrls(pending);
      setPendingImageItems([]);
      return base;
    }

    const newPaths = [];
    for (const p of batch) {
      if (!isAllowedNotifMediaFile(p.file)) {
        throw new Error(
          `Type non accepté (${NOTIF_MEDIA_TYPE_HINT}) : ${p.file.name}`,
        );
      }
      if (p.file.size > notifMediaMaxBytes(p.file)) {
        throw new Error(
          `Fichier trop volumineux (max ${notifMediaMaxLabel(p.file)}) : ${p.file.name}`,
        );
      }
      const res = await uploadHubupNotificationImage(p.file);
      const path = String(res?.data?.path ?? '').trim();
      if (!path) throw new Error('Pas de chemin');
      newPaths.push(path);
    }

    revokePendingObjectUrls(batch);
    setPendingImageItems((prev) =>
      prev.filter((x) => !batch.some((b) => b.id === x.id)),
    );

    const merged = normalizeFormImageUrls([...base, ...newPaths]);
    setForm((f) => ({ ...f, imageUrls: merged }));
    return merged;
  };

  const uploadBodyInlineImage = useCallback(async (file) => {
    if (isNotifLargeMediaFile(file) || !isAllowedNotifMediaFile(file)) {
      throw new Error(
        'Images uniquement dans le message (PNG, JPEG, WebP ou GIF).',
      );
    }
    if (file.size > NOTIF_IMAGE_MAX_BYTES) {
      throw new Error(`Image trop volumineuse (max 3 Mo) : ${file.name}`);
    }
    const res = await uploadHubupNotificationImage(file);
    const path = String(res?.data?.path ?? '').trim();
    if (!path) throw new Error('Upload image impossible.');
    return resolveHubupAssetUrl(path);
  }, []);

  const handleBodyInlineImageUpload = useCallback(
    async (file) => {
      try {
        return await uploadBodyInlineImage(file);
      } catch (err) {
        setFormError(
          err?.message || err?.data?.message || 'Upload image impossible.',
        );
        throw err;
      }
    },
    [uploadBodyInlineImage],
  );

  const toggleBody = (id) => {
    setExpandedBody((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const closeGenerateModal = () => {
    if (generateLoading) {
      deferHubupGenerateOpenForm();
    }
    setGenerateMediaReview(null);
    setGenerateMediaImportError(null);
    setGenerateBrowserOpen(false);
  };

  const openGenerateModal = () => {
    setGenerateError(null);
    setGenerateBrowserOpen(true);
  };

  const openCreate = () => {
    if (
      !isAdmin &&
      (!joinWelcomeConfigured || topicsSelectable.length === 0)
    ) {
      setHint(
        'Créez au moins une rubrique et définissez le message de bienvenue à l’adhésion dans « Mes rubriques ».',
      );
      setTimeout(() => setHint(''), 6000);
      return;
    }
    setEditReschedule(false);
    setIsDuplicateDraft(false);
    setEditingDraftId(null);
    setEditing(null);
    setEditingSchedule(null);
    setScheduleSend(false);
    setScheduleDate('');
    setScheduleTime('09:00');
    setNotifyPush(true);
    setIsPrivateNotification(false);
    resetAnnuaireRecipientState();
    setForm({
      ...emptyForm(),
      topicId:
        topicsSelectable[0]?.id || (isAdmin ? SUBJECT_NOTIFY_EVERYONE : ''),
    });
    setFormError(null);
    resetLinkedFormState();
    clearPendingImages();
    setShowForm(true);
  };

  const openEdit = (m) => {
    setIsDuplicateDraft(false);
    setEditingDraftId(null);
    setEditReschedule(false);
    setEditing(m);
    setEditingSchedule(null);
    setScheduleSend(false);
    const sd = (m.scheduleDateParis || '').trim();
    const st = (m.scheduleTimeParis || '').trim();
    setScheduleDate(sd || '');
    setScheduleTime(st || '09:00');
    setNotifyPush(m.notifyPush !== false);
    setIsPrivateNotification(m.private === true);
    applyTargetVisitorsFromRow(m);
    setForm({
      topicId: m.topicId,
      title: m.title,
      bodyHtml: messageBodyEditorInitialHtml(m),
      linkUrl: m.linkUrl || '',
      imageUrls: messageImageUrlsFromModel(m),
      priority: m.priority === 'high' ? 'high' : 'normal',
    });
    setFormError(null);
    resetLinkedFormState();
    clearPendingImages();
    setShowForm(true);
    if (m.formId) {
      void loadMessageLinkedForm(m.id, true);
    }
    if (m.calendarEvent) {
      applyLinkedEventFromApi(m.calendarEvent, true);
    } else {
      void loadMessageLinkedEvent(m.id, false);
    }
  };

  const openEditSchedule = (s) => {
    setIsDuplicateDraft(false);
    setEditingDraftId(null);
    setEditReschedule(false);
    setEditing(null);
    setEditingSchedule(s);
    setScheduleSend(true);
    setScheduleDate(s.scheduleDateParis || minScheduleDateInputParis());
    setScheduleTime(s.scheduleTimeParis || '09:00');
    let topicVal = '';
    if (s.allTopics) {
      topicVal = SUBJECT_NOTIFY_EVERYONE;
    } else if (s.topicIds?.length) {
      topicVal = s.topicIds[0];
    }
    setForm({
      topicId: topicVal,
      title: s.title || '',
      bodyHtml: messageBodyEditorInitialHtml({
        body: s.body,
        bodyHtml: s.bodyHtml,
      }),
      linkUrl: s.linkUrl || '',
      imageUrls: scheduleImageUrlsFromModel(s),
      priority: s.priority === 'high' ? 'high' : 'normal',
    });
    setNotifyPush(s.notifyPush !== false);
    setIsPrivateNotification(s.private === true);
    applyTargetVisitorsFromRow(s);
    setFormError(null);
    resetLinkedFormState();
    if (s.form !== undefined && s.form !== null) {
      applyLinkedFormFromApi(s.form, true);
    }
    if (s.calendarEvent !== undefined && s.calendarEvent !== null) {
      applyLinkedEventFromApi(s.calendarEvent, true);
    }
    clearPendingImages();
    setShowForm(true);
  };

  const openDuplicateFromMessage = (m) => {
    if (
      !isAdmin &&
      (!joinWelcomeConfigured || topicsSelectable.length === 0)
    ) {
      setHint(
        'Créez au moins une rubrique et définissez le message de bienvenue à l’adhésion dans « Mes rubriques ».',
      );
      setTimeout(() => setHint(''), 6000);
      return;
    }
    setEditing(null);
    setEditingSchedule(null);
    setIsDuplicateDraft(true);
    setEditingDraftId(null);
    setEditReschedule(false);
    setScheduleSend(false);
    setScheduleDate('');
    setScheduleTime('09:00');
    setNotifyPush(m.notifyPush !== false);
    setIsPrivateNotification(m.private === true);
    resetAnnuaireRecipientState();

    let nextTopicId = (m.topicId || '').trim();
    if (
      !isAdmin &&
      nextTopicId &&
      topics.some((t) => t.id === nextTopicId && isHubupEditorTopic(t))
    ) {
      nextTopicId = '';
    }
    if (m.topicTitle === 'Notifier tout le monde') {
      nextTopicId = isAdmin
        ? SUBJECT_NOTIFY_EVERYONE
        : topicsSelectable[0]?.id || '';
    }
    if (!isAdmin && nextTopicId === SUBJECT_NOTIFY_EVERYONE) {
      nextTopicId = topicsSelectable[0]?.id || '';
    }
    if (
      !isAdmin &&
      nextTopicId &&
      !topicsSelectable.some((t) => t.id === nextTopicId)
    ) {
      nextTopicId = topicsSelectable[0]?.id || '';
    }

    setForm({
      ...emptyForm(),
      topicId: nextTopicId,
      title: m.title || '',
      bodyHtml: messageBodyEditorInitialHtml(m),
      linkUrl: m.linkUrl || '',
      imageUrls: messageImageUrlsFromModel(m),
      priority: m.priority === 'high' ? 'high' : 'normal',
    });
    setFormError(null);
    clearPendingImages();
    resetLinkedFormState();
    setShowForm(true);
    if (m.formId) {
      void loadMessageLinkedForm(m.id, true);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setIsDuplicateDraft(false);
    setEditingDraftId(null);
    setEditing(null);
    setEditingSchedule(null);
    setEditReschedule(false);
    setFormError(null);
    setScheduleSend(false);
    setScheduleDate('');
    setScheduleTime('09:00');
    setNotifyPush(true);
    setIsPrivateNotification(false);
    resetAnnuaireRecipientState();
    resetLinkedFormState();
    clearPendingImages();
    setDraftAiMediaReview(null);
  };

  const openDraft = (d) => {
    if (
      !isAdmin &&
      (!joinWelcomeConfigured || topicsSelectable.length === 0)
    ) {
      setHint(
        'Créez au moins une rubrique et définissez le message de bienvenue à l’adhésion dans « Mes rubriques ».',
      );
      setTimeout(() => setHint(''), 6000);
      return;
    }
    setEditing(null);
    setEditingSchedule(null);
    setIsDuplicateDraft(false);
    setEditReschedule(false);
    setEditingDraftId(d._id);
    let tid = String(d.topicId || '').trim();
    if (!isAdmin && tid === SUBJECT_NOTIFY_EVERYONE) {
      tid = topicsSelectable[0]?.id || '';
    }
    setScheduleSend(!!d.scheduleSend);
    setScheduleDate((d.scheduleDate || '').trim() || '');
    setScheduleTime((d.scheduleTime || '').trim() || '09:00');
    setNotifyPush(d.notifyPush !== false);
    setIsPrivateNotification(d.private === true);
    applyTargetVisitorsFromRow(d);
    setForm({
      topicId: tid,
      title: d.title || '',
      bodyHtml: d.bodyHtml && String(d.bodyHtml).trim() ? d.bodyHtml : '<p></p>',
      linkUrl: d.linkUrl || '',
      imageUrls: normalizeFormImageUrls(Array.isArray(d.imageUrls) ? d.imageUrls : []),
      priority: d.priority === 'high' ? 'high' : 'normal',
    });
    setFormError(null);
    resetLinkedFormState();
    if (d.form) {
      applyLinkedFormFromApi(d.form, true);
    }
    if (d.calendarEvent) {
      applyLinkedEventFromApi(d.calendarEvent, true);
    }
    clearPendingImages();
    const suggested = Array.isArray(d.aiSuggestedMedia) ? d.aiSuggestedMedia : [];
    if (
      suggested.length > 0 &&
      !(Array.isArray(d.imageUrls) && d.imageUrls.length > 0)
    ) {
      setDraftAiMediaReview({ draftId: d._id, items: suggested, draft: d });
    } else {
      setDraftAiMediaReview(null);
    }
    setShowForm(true);
  };

  const clearAiSuggestedMediaOnDraft = async (draftId) => {
    if (!groupId || !draftId) return;
    try {
      await updateHubupNotificationDraft(groupId, draftId, {
        aiSuggestedMedia: [],
      });
    } catch {
      /* ignore */
    }
  };

  const applyAiMediaSelection = async (
    { draftId, draft: draftBase },
    selectedUrls,
    { updateForm = false } = {},
  ) => {
    if (!groupId || !draftId) return null;
    setGenerateMediaImportError(null);
    setGenerateMediaImportLoading(true);
    try {
      let imageUrls = normalizeFormImageUrls(draftBase?.imageUrls ?? []);
      const urls = Array.isArray(selectedUrls)
        ? selectedUrls.filter(Boolean)
        : [];
      if (urls.length) {
        const res = await importHubupNotificationMediaFromUrls(groupId, {
          urls,
          existingCount: imageUrls.length,
        });
        const paths = res?.data?.paths ?? [];
        const errors = res?.data?.errors ?? [];
        if (!paths.length && errors.length) {
          const msg = errors[0] || 'Import média impossible.';
          setGenerateMediaImportError(msg);
          if (updateForm) setFormError(msg);
          return null;
        }
        imageUrls = normalizeFormImageUrls([...imageUrls, ...paths]);
        if (errors.length) {
          setFormError(
            `Certains médias n’ont pas pu être importés : ${errors[0]}`,
          );
        }
      }
      const upd = await updateHubupNotificationDraft(groupId, draftId, {
        imageUrls,
        aiSuggestedMedia: [],
      });
      return (
        upd?.data?.draft ?? {
          ...draftBase,
          imageUrls,
          aiSuggestedMedia: [],
        }
      );
    } finally {
      setGenerateMediaImportLoading(false);
    }
  };

  const finishGenerateMediaReview = async (selectedUrls) => {
    if (!generateMediaReview) return;
    const updated = await applyAiMediaSelection(generateMediaReview, selectedUrls);
    if (!updated) return;
    setGenerateMediaReview(null);
    setGenerateMediaImportError(null);
    setGenerateBrowserOpen(false);
    await load();
    openDraft(updated);
  };

  const skipGenerateMediaReview = async () => {
    if (!generateMediaReview) return;
    const { draftId, draft } = generateMediaReview;
    await clearAiSuggestedMediaOnDraft(draftId);
    setGenerateMediaReview(null);
    setGenerateMediaImportError(null);
    setGenerateBrowserOpen(false);
    await load();
    openDraft({ ...draft, aiSuggestedMedia: [] });
  };

  const finishDraftAiMediaReview = async (selectedUrls) => {
    if (!draftAiMediaReview) return;
    const updated = await applyAiMediaSelection(draftAiMediaReview, selectedUrls, {
      updateForm: true,
    });
    if (!updated) return;
    setDraftAiMediaReview(null);
    setForm((f) => ({
      ...f,
      imageUrls: normalizeFormImageUrls(updated.imageUrls ?? []),
    }));
    await load();
  };

  const skipDraftAiMediaReview = async () => {
    if (!draftAiMediaReview) return;
    await clearAiSuggestedMediaOnDraft(draftAiMediaReview.draftId);
    setDraftAiMediaReview(null);
  };

  const handleGenerateFromUrl = async ({ url: urlInput, language = 'fr' } = {}) => {
    const url = normalizeGenerateUrlInput(urlInput);
    if (!url || !groupId) {
      setGenerateError('Indiquez une URL valide.');
      return;
    }
    setGenerateLoading(true);
    setGenerateError(null);
    setGenerateBrowserOpen(true);

    const result = await runHubupGenerateNotificationJob(groupId, url, language);

    if (!pageMountedRef.current) {
      return;
    }

    setGenerateLoading(false);

    if (!result.ok) {
      setGenerateError(
        result.error?.message || 'Impossible de préparer la notification.',
      );
      setGenerateBrowserOpen(true);
      return;
    }

    const suggested =
      (Array.isArray(result.suggestedMedia) && result.suggestedMedia.length
        ? result.suggestedMedia
        : null) ??
      (Array.isArray(result.draft?.aiSuggestedMedia) &&
      result.draft.aiSuggestedMedia.length
        ? result.draft.aiSuggestedMedia
        : []);

    if (result.openFormWhenDone && result.draft && suggested.length > 0) {
      await load();
      setGenerateMediaReview({
        draftId: result.draft._id,
        items: suggested,
        draft: result.draft,
      });
      setGenerateMediaImportError(null);
      setGenerateBrowserOpen(true);
      return;
    }

    setGenerateBrowserOpen(false);
    setGenerateMediaReview(null);

    if (result.openFormWhenDone && result.draft) {
      await load();
      openDraft(result.draft);
      return;
    }

    if (result.draft) {
      await load();
      setHint(
        'Notification enregistrée en brouillon. Retrouvez-la dans la liste des brouillons.',
      );
      setTimeout(() => setHint(''), 8000);
    }
  };

  const handleImageFile = (ev) => {
    const input = ev.target;
    const filesArr = Array.from(input?.files || []);
    input.value = '';
    if (!filesArr.length) return;

    const prev = pendingFilesRef.current;
    const urlN = formRef.current.imageUrls?.length ?? 0;
    let remaining = NOTIF_IMAGES_MAX - urlN - prev.length;

    if (remaining <= 0) {
      setFormError(`Maximum ${NOTIF_IMAGES_MAX} fichiers.`);
      return;
    }

    const toAdd = [];
    const errors = [];
    let skippedForLimit = 0;

    for (const file of filesArr) {
      if (toAdd.length >= remaining) {
        skippedForLimit++;
        continue;
      }
      if (!isAllowedNotifMediaFile(file)) {
        errors.push(
          `« ${file.name} » : type non accepté (${NOTIF_MEDIA_TYPE_HINT}).`,
        );
        continue;
      }
      if (file.size > notifMediaMaxBytes(file)) {
        errors.push(
          `« ${file.name} » dépasse ${notifMediaMaxLabel(file)} (limite BO).`,
        );
        continue;
      }
      toAdd.push({
        id: `p-${file.lastModified}-${file.size}-${toAdd.length}-${Date.now()}`,
        file,
        objectUrl: URL.createObjectURL(file),
      });
    }

    if (skippedForLimit > 0) {
      errors.push(
        `${skippedForLimit} fichier(s) ignoré(s) (limite ${NOTIF_IMAGES_MAX} fichiers au total).`,
      );
    }

    if (!toAdd.length) {
      setFormError(errors.length ? errors.join(' ') : null);
      return;
    }

    setFormError(errors.length ? errors.join(' ') : null);
    setPendingImageItems((p) => [...p, ...toAdd]);
  };

  const removePendingImageItem = (id) => {
    setPendingImageItems((prev) => {
      const found = prev.find((x) => x.id === id);
      if (found) {
        try {
          URL.revokeObjectURL(found.objectUrl);
        } catch {}
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  const handleSaveDraft = async () => {
    if (editing || editingSchedule) return;
    const titleTrim = form.title.trim();
    if (!titleTrim) {
      setFormError('Le titre est requis pour un brouillon.');
      return;
    }
    if (
      !notificationBodyRequirementMet({
        bodyHtml: form.bodyHtml,
        formEnabled,
        formValidated,
        eventEnabled,
        eventValidated,
        imageUrls: form.imageUrls,
        pendingMediaCount: pendingFilesRef.current.length,
      })
    ) {
      setFormError(NOTIF_BODY_OR_MEDIA_MSG);
      return;
    }
    const tid = (form.topicId || '').trim();
    if (!tid) {
      setFormError('Choisissez une rubrique.');
      return;
    }
    if (!isAdmin && tid === SUBJECT_NOTIFY_EVERYONE) {
      setFormError('Choisissez une rubrique de cette communauté.');
      return;
    }
    const draftExtrasResult = resolveExtrasPayloadForSave();
    if (draftExtrasResult.error) {
      setFormError(draftExtrasResult.error);
      return;
    }

    setSavingDraft(true);
    setFormError(null);
    try {
      let imageUrlsNorm = formRef.current.imageUrls || [];
      if (pendingFilesRef.current.length) {
        imageUrlsNorm = await uploadPendingAndMergeUrls(formRef.current.imageUrls);
      }
      const payload = mergeTargetVisitorIds({
        topicId: form.topicId,
        title: titleTrim,
        bodyHtml: form.bodyHtml || '',
        linkUrl: form.linkUrl || '',
        imageUrls: imageUrlsNorm,
        priority: form.priority,
        notifyPush,
        private: isPrivateNotification,
        scheduleSend,
        scheduleDate: scheduleSend ? scheduleDate : '',
        scheduleTime: scheduleSend ? scheduleTime : '',
        ...draftExtrasResult.payload,
      });
      if (editingDraftId) {
        await updateHubupNotificationDraft(groupId, editingDraftId, payload);
      } else {
        const res = await createHubupNotificationDraft(groupId, payload);
        const id = res?.data?.draft?._id;
        if (id) setEditingDraftId(id);
      }
      setHint('Brouillon enregistré.');
      setTimeout(() => setHint(''), 4000);
      await load();
    } catch (err) {
      setFormError(
        err?.message || err?.data?.message || 'Enregistrement du brouillon impossible.',
      );
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePublishDraftFromTable = async (draftId) => {
    if (!groupId || !draftId) return;
    const d = drafts.find((x) => x._id === draftId);
    if (quotaExhausted && d?.private !== true) {
      setError(QUOTA_MSG);
      return;
    }
    if (!window.confirm(`Publier « ${d?.title || 'Sans titre'} » ?`)) return;
    setSaving(true);
    setError(null);
    try {
      await publishHubupNotificationDraft(groupId, draftId);
      setHint('Notification publiée depuis le brouillon.');
      setTimeout(() => setHint(''), 5000);
      await load();
      await refetchCommunities();
    } catch (err) {
      setError(err?.message || 'Publication du brouillon impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDraftRow = async (draftId) => {
    if (!groupId || !draftId) return;
    if (!window.confirm('Supprimer ce brouillon ?')) return;
    setError(null);
    try {
      await deleteHubupNotificationDraft(groupId, draftId);
      await load();
    } catch (e) {
      setError(e?.message || 'Suppression du brouillon impossible.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formPanel === 'form') {
      finishFormEditor();
      return;
    }
    if (formPanel === 'event') {
      finishEventEditor();
      return;
    }
    if (limitToAnnuaire && targetVisitorIds.length === 0) {
      setFormError('Choisissez au moins une personne dans l’annuaire.');
      setRecipientModalOpen(true);
      return;
    }
    const title = form.title.trim();
    if (!title) {
      setFormError('Le titre est requis.');
      return;
    }
    if (
      !notificationBodyRequirementMet({
        bodyHtml: form.bodyHtml,
        formEnabled,
        formValidated,
        eventEnabled,
        eventValidated,
        imageUrls: form.imageUrls,
        pendingMediaCount: pendingFilesRef.current.length,
      })
    ) {
      setFormError(NOTIF_BODY_OR_MEDIA_MSG);
      return;
    }

    if (
      quotaExhausted &&
      !isPrivateNotification &&
      !editing &&
      !scheduleSend &&
      !editingSchedule &&
      !(editing && editReschedule)
    ) {
      setFormError(QUOTA_MSG);
      return;
    }

    const extrasPayloadResult = resolveExtrasPayloadForSave();
    if (extrasPayloadResult.error) {
      setFormError(extrasPayloadResult.error);
      return;
    }
    const formPayload = extrasPayloadResult.payload;
    if (
      formEnabled &&
      formValidated &&
      !Object.prototype.hasOwnProperty.call(formPayload, 'form')
    ) {
      setFormError(
        'Le formulaire n’a pas pu être préparé pour l’envoi. Vérifiez-le puis validez à nouveau.',
      );
      return;
    }

    if (editingDraftId && !editing && !editingSchedule) {
      setFormError(null);
      setSaving(true);
      try {
        let imageUrlsDraft;
        try {
          imageUrlsDraft = await uploadPendingAndMergeUrls(formRef.current.imageUrls);
        } catch (err) {
          setFormError('Envoi des illustrations impossible.');
          setSaving(false);
          return;
        }
        await updateHubupNotificationDraft(
          groupId,
          editingDraftId,
          mergeTargetVisitorIds({
            topicId: form.topicId,
            title: form.title.trim(),
            bodyHtml: form.bodyHtml || '',
            linkUrl: form.linkUrl || '',
            imageUrls: imageUrlsDraft,
            priority: form.priority,
            notifyPush,
            private: isPrivateNotification,
            scheduleSend,
            scheduleDate: scheduleSend ? scheduleDate : '',
            scheduleTime: scheduleSend ? scheduleTime : '',
            ...formPayload,
          }),
        );
        await publishHubupNotificationDraft(groupId, editingDraftId);
        closeForm();
        setHint('Notification publiée.');
        setTimeout(() => setHint(''), 5000);
        await load();
        await refetchCommunities();
      } catch (err) {
        setFormError(
          err?.message || err?.data?.message || 'Publication impossible.',
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    if (editingSchedule) {
      const topicIdEd = (form.topicId || '').trim();
      if (!isAdmin && topicIdEd === SUBJECT_NOTIFY_EVERYONE) {
        setFormError(
          'La diffusion à tous les abonnés HubUP est réservée aux administrateurs. Choisissez une rubrique de cette communauté.',
        );
        return;
      }
      if (!topicIdEd) {
        setFormError('Choisissez une rubrique.');
        return;
      }
      const schedIsBroadcastAll =
        isAdmin && topicIdEd === SUBJECT_NOTIFY_EVERYONE;
      if (!schedIsBroadcastAll) {
        if (!joinWelcomeConfigured) {
          setFormError(
            'Définissez le message de bienvenue à l’adhésion dans « Mes rubriques » avant d’enregistrer.',
          );
          return;
        }
        if (!isAdmin && topicsSelectable.length === 0) {
          setFormError(
            'Cette communauté n’a aucune rubrique utilisable. Créez-en une dans « Mes rubriques ».',
          );
          return;
        }
        if (isAdmin && topics.length === 0) {
          setFormError(
            'Cette communauté n’a aucune rubrique. Créez-en une dans « Mes rubriques ».',
          );
          return;
        }
      }
      if (!scheduleDate.trim() || !scheduleTime.trim()) {
        setFormError('Choisis une date et une heure pour la planification (Paris).');
        return;
      }
      setFormError(null);
      setSaving(true);
      try {
        let imageUrlsForPatch;
        try {
          imageUrlsForPatch = await uploadPendingAndMergeUrls(
            formRef.current.imageUrls,
          );
        } catch (err) {
          setFormError('Envoi des illustrations impossible.');
          setSaving(false);
          return;
        }
        const patch = mergeTargetVisitorIds({
          title,
          bodyHtml: form.bodyHtml || '',
          priority: form.priority === 'high' ? 'high' : 'normal',
          linkUrl: (form.linkUrl || '').trim(),
          imageUrls: imageUrlsForPatch,
          scheduleDate: scheduleDate.trim(),
          scheduleTime: scheduleTime.trim(),
          ...formPayload,
        });
        if (isAdmin) {
          patch.notifyPush = notifyPush;
        }
        patch.private = isPrivateNotification;
        if (isAdmin && topicIdEd === SUBJECT_NOTIFY_EVERYONE) {
          patch.topicIds = ['all'];
        } else {
          patch.topicIds = [topicIdEd];
        }
        await patchHubupGroupScheduledNotification(groupId, editingSchedule._id, patch);
        closeForm();
        setHint('Planification mise à jour.');
        setTimeout(() => setHint(''), 4000);
        await load();
      } catch (err) {
        setFormError(parseHubupApiError(err, 'Enregistrement impossible.'));
      } finally {
        setSaving(false);
      }
      return;
    }

    const topicId = (form.topicId || '').trim();
    if (!editing && !isAdmin && topicId === SUBJECT_NOTIFY_EVERYONE) {
      setFormError(
        'La diffusion à tous les abonnés HubUP est réservée aux administrateurs. Choisissez une rubrique de cette communauté.',
      );
      return;
    }
    const isBroadcastAll =
      !editing && isAdmin && topicId === SUBJECT_NOTIFY_EVERYONE;
    if (!editing && !isBroadcastAll) {
      if (!joinWelcomeConfigured) {
        setFormError(
          'Définissez le message de bienvenue à l’adhésion dans « Mes rubriques » avant de publier.',
        );
        return;
      }
      if (!isAdmin && topicsSelectable.length === 0) {
        setFormError(
          'Cette communauté n’a aucune rubrique utilisable. Créez-en une dans « Mes rubriques ».',
        );
        return;
      }
      if (isAdmin && topics.length === 0) {
        setFormError(
          'Cette communauté n’a aucune rubrique. Créez-en une ou utilisez « Notifier tout le monde ».',
        );
        return;
      }
    }
    if (!editing && !topicId) {
      setFormError('Choisissez une rubrique.');
      return;
    }
    if (!editing && scheduleSend) {
      if (!scheduleDate.trim() || !scheduleTime.trim()) {
        setFormError('Choisissez une date et une heure pour la planification (heure de Paris).');
        return;
      }
    }
    setFormError(null);
    setSaving(true);
    try {
      let imageUrlsNorm;
      try {
        imageUrlsNorm = await uploadPendingAndMergeUrls(
          formRef.current.imageUrls,
        );
      } catch (err) {
        setFormError('Envoi des illustrations impossible.');
        setSaving(false);
        return;
      }
      const payload = mergeTargetVisitorIds({
        title,
        bodyHtml: form.bodyHtml || '',
        priority: form.priority === 'high' ? 'high' : 'normal',
        linkUrl: (form.linkUrl || '').trim(),
        private: isPrivateNotification,
        ...(isAdmin && !notifyPush && !isPrivateNotification ? { notifyPush: false } : {}),
        ...formPayload,
      });
      const payloadWithImages =
        imageUrlsNorm.length > 0
          ? { ...payload, imageUrls: imageUrlsNorm }
          : payload;
      if (editing) {
        if (editReschedule) {
          if (!scheduleDate.trim() || !scheduleTime.trim()) {
            setFormError(
              'Choisissez une date et une heure pour la replanification (Europe/Paris).',
            );
            setSaving(false);
            return;
          }
        }
        const updateRes = await updateHubupMessage(
          editing.id,
          mergeTargetVisitorIds({
          ...payload,
            imageUrls: imageUrlsNorm,
          topicId: editing.topicId,
          topicTitle: editing.topicTitle,
            private: isPrivateNotification,
            ...(editReschedule
              ? {
                  scheduleDate: scheduleDate.trim(),
                  scheduleTime: scheduleTime.trim(),
                }
              : {}),
          }),
        );
      closeForm();
        const updateData = updateRes?.data;
      setHint(
          editReschedule
            ? scheduledSuccessHint(updateData) ||
                'Notification mise à jour et envoi replanifié (heure de Paris).'
            : 'Notification mise à jour.',
        );
        setTimeout(() => setHint(''), editReschedule ? 5000 : 3000);
      await load();
      } else if (isBroadcastAll) {
        const body =
          scheduleSend
            ? {
                ...payloadWithImages,
                scheduleDate: scheduleDate.trim(),
                scheduleTime: scheduleTime.trim(),
              }
            : payloadWithImages;
        const res = await createHubupNotificationAllTopics(body);
        const d = res?.data;
        closeForm();
        const schedHint = scheduledSuccessHint(d);
        if (schedHint) {
          setHint(schedHint);
      } else {
          setHint('Notification envoyée à tous les abonnés (toutes les rubriques HubUP).');
          await refetchCommunities();
        }
        setTimeout(() => setHint(''), 5000);
        await load();
      } else {
        const body =
          scheduleSend
            ? {
                ...payloadWithImages,
                scheduleDate: scheduleDate.trim(),
                scheduleTime: scheduleTime.trim(),
              }
            : payloadWithImages;
        const res = await createHubupTopicMessage(groupId, topicId, body);
        const d = res?.data;
      closeForm();
        const schedHint = scheduledSuccessHint(d);
        if (schedHint) {
          setHint(schedHint);
        } else {
          setHint('Notification publiée.');
          await refetchCommunities();
        }
        setTimeout(() => setHint(''), 5000);
      await load();
      }
    } catch (err) {
      setFormError(parseHubupApiError(err, 'Enregistrement impossible.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer la notification « ${m.title} » ?`)) return;
    setError(null);
    try {
      await deleteHubupMessage(m.id);
      await load();
    } catch (err) {
      setError('Suppression impossible.');
    }
  };

  const handleDeleteSchedule = async (s) => {
    if (s.allTopics && !isAdmin) {
      setError(
        'Supprimer une planification « toutes les rubriques » est réservé aux administrateurs.',
      );
      return;
    }
    if (!window.confirm(`Annuler la planification « ${s.title} » ?`)) return;
    setError(null);
    try {
      await deleteHubupGroupScheduledNotification(groupId, s._id);
      setHint('Planification annulée.');
      setTimeout(() => setHint(''), 4000);
      await load();
    } catch (err) {
      setError('Suppression de la planification impossible.');
    }
  };

  const handleResend = async (m) => {
    if (quotaExhausted) {
      setError(QUOTA_MSG);
      return;
    }
    const wasPrivate = m.private === true;
    if (wasPrivate) {
      const ok = window.confirm(
        'Cette annonce est privée. Le push la rendra publique (fil HubUP) puis l’enverra aux abonnés. Continuer ?',
      );
      if (!ok) return;
    }
    setResendId(m.id);
    setError(null);
    try {
      if (wasPrivate) {
        await updateHubupMessage(m.id, {
          title: m.title,
          bodyHtml: messageBodyEditorInitialHtml(m),
          priority: m.priority === 'high' ? 'high' : 'normal',
          linkUrl: (m.linkUrl || '').trim(),
          imageUrls: messageImageUrlsFromModel(m),
          topicId: m.topicId,
          topicTitle: m.topicTitle,
          private: false,
          notifyPush: true,
        });
      }
      const res = await resendHubupMessagePush(m.id, groupId);
      const d = res?.data;
      const sent = d?.push?.sent ?? 0;
      const failed = d?.push?.failed ?? 0;
      setHint(
        wasPrivate
          ? `Annonce passée en public · push : ${sent} envoyé(s)${
          failed ? `, ${failed} échec(s)` : ''
            } · ${d?.targetVisitors ?? 0} visiteur${(d?.targetVisitors ?? 0) === 1 ? '' : 's'} ciblé${(d?.targetVisitors ?? 0) === 1 ? '' : 's'}.`
          : `Push renvoyé : ${sent} envoyé(s)${
              failed ? `, ${failed} échec(s)` : ''
            } · ${d?.targetVisitors ?? 0} visiteur${(d?.targetVisitors ?? 0) === 1 ? '' : 's'} ciblé${(d?.targetVisitors ?? 0) === 1 ? '' : 's'}.`,
      );
      setTimeout(() => setHint(''), 5000);
      if (wasPrivate) await load();
    } catch (err) {
      setError('Renvoi des push impossible.');
    } finally {
      setResendId(null);
    }
  };

  const openPublicLinkModal = (m) => {
    const url = buildHubupNotificationUrl(m?.id);
    if (!url) {
      setHint('Lien indisponible pour cette notification.');
      setTimeout(() => setHint(''), 4000);
      return;
    }
    setPublicLinkCopyHint('');
    setPublicLinkModal({
      url,
      title: String(m?.title || '').trim() || 'Notification',
    });
  };

  const copyPublicLinkFromModal = async () => {
    const url = publicLinkModal?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setPublicLinkCopyHint('Lien copié.');
    } catch {
      setPublicLinkCopyHint('Impossible de copier le lien.');
    }
  };

  const colSpan = canManageHubup ? 7 : 6;
  const scheduledColSpan = canManageHubup ? 5 : 4;

  return (
    <>
      <Helmet>
        <title>Mes notifications — HubUP</title>
      </Helmet>
      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
            Mes notifications
          </h1>
          {groupMetaLoaded ? (
            <div className="mb-4 rounded-lg border border-border bg-card shadow-elevation-1 overflow-hidden">
              {canManageHubup ? (
                <>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
                    onClick={toggleFeedBrandPanel}
                    aria-expanded={feedBrandOpen}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon
                        name={feedBrandOpen ? 'ChevronDown' : 'ChevronRight'}
                        size={16}
                        className="shrink-0 text-muted-foreground"
                      />
                      <span className="text-sm font-semibold text-foreground">
                        Style des annonces (fil)
                      </span>
                    </div>
                    <span
                      className={`text-[10px] font-medium rounded-full border px-2 py-0.5 shrink-0 ${
                        String(feedFont || '').trim()
                          ? 'bg-primary/10 text-primary border-primary/30'
                          : 'bg-muted text-muted-foreground border-border'
                      }`}
                    >
                      {feedBrandStatusLabel}
                    </span>
                  </button>
                  {feedBrandOpen ? (
                    <div className="px-4 pb-4 pt-1 border-t border-border space-y-3">
                      {feedBrandError ? (
                        <p className="text-xs text-error">{feedBrandError}</p>
                      ) : null}
                      {feedBrandHint ? (
                        <p className="text-xs text-muted-foreground">{feedBrandHint}</p>
                      ) : null}
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Personnalise l&apos;affichage des cartes dans le fil HubUP. Choisis une
                        police pour activer le style ; les couleurs sont optionnelles.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                          <label
                            htmlFor="hubup-feed-brand-font"
                            className="text-xs font-medium text-foreground mb-1 block"
                          >
                            Police
                          </label>
                          <select
                            id="hubup-feed-brand-font"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                            value={feedFont}
                            disabled={feedBrandSaving}
                            onChange={(e) => setFeedFont(e.target.value)}
                            style={
                              feedFont
                                ? { fontFamily: feedFont }
                                : undefined
                            }
                          >
                            {feedFontOptions.map((opt) => (
                              <option
                                key={opt.value || '__default__'}
                                value={opt.value}
                                style={opt.value ? { fontFamily: opt.value } : undefined}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <HexColorField
                          id="hubup-feed-brand-bg"
                          label="Couleur de fond (carte)"
                          value={feedColorHexBg}
                          disabled={feedBrandSaving}
                          placeholder="#010064"
                          onChange={setFeedColorHexBg}
                        />
                        <HexColorField
                          id="hubup-feed-brand-font-color"
                          label="Couleur du texte"
                          value={feedColorHexFont}
                          disabled={feedBrandSaving}
                          placeholder="#333483"
                          onChange={setFeedColorHexFont}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          iconName="Save"
                          iconPosition="left"
                          disabled={feedBrandSaving}
                          onClick={() => void handleSaveFeedBrand()}
                        >
                          {feedBrandSaving ? 'Enregistrement…' : 'Enregistrer'}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
              {canManageHubup && catchallAllowed ? (
                <>
                  <button
                    type="button"
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors ${
                      canManageHubup ? 'border-t border-border' : ''
                    }`}
                    onClick={toggleCatchallPanel}
                    aria-expanded={catchallOpen}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon
                        name={catchallOpen ? 'ChevronDown' : 'ChevronRight'}
                        size={16}
                        className="shrink-0 text-muted-foreground"
                      />
                      <span className="text-sm font-semibold text-foreground">
                        Configuration de l'E-mail
                      </span>
                    </div>
                    <span
                      className={`text-[10px] font-medium rounded-full border px-2 py-0.5 shrink-0 ${
                        String(catchallSenderEmail || '').trim()
                          ? 'bg-primary/10 text-primary border-primary/30'
                          : 'bg-muted text-muted-foreground border-border'
                      }`}
                    >
                      {catchallStatusLabel}
                    </span>
                  </button>
                  {catchallOpen ? (
                    <div className="px-4 pb-4 pt-1 border-t border-border space-y-3">
                      {catchallError ? (
                        <p className="text-xs text-error">{catchallError}</p>
                      ) : null}
                      {catchallHint ? (
                        <p className="text-xs text-muted-foreground">{catchallHint}</p>
                      ) : null}
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        E-mail expéditeur pour cette communauté : les mails
                        reçus sur la boîte mail 'hubup@deepblock.fr' depuis cette adresse
                        publieront une notification.
                      </p>
                      <div>
                        <label
                          htmlFor="hubup-catchall-sender-email"
                          className="text-xs font-medium text-foreground mb-1 block"
                        >
                          E-mail expéditeur
                        </label>
                        <input
                          id="hubup-catchall-sender-email"
                          type="email"
                          autoComplete="email"
                          className="w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                          value={catchallSenderEmail}
                          disabled={catchallSaving}
                          placeholder="vous@entreprise.fr"
                          onChange={(e) => setCatchallSenderEmail(e.target.value)}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          iconName="Save"
                          iconPosition="left"
                          disabled={catchallSaving || !catchallAllowed}
                          onClick={() => void handleSaveCatchallSender()}
                        >
                          {catchallSaving ? 'Enregistrement…' : 'Enregistrer'}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
          {error ? <p className="text-xs text-error mb-2">{error}</p> : null}
          {generateLoading && !generateBrowserOpen ? (
            <div
              className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 flex items-start gap-3"
              role="status"
            >
              <Icon
                name="Loader2"
                size={20}
                className="text-primary animate-spin shrink-0 mt-0.5"
              />
              <div className="text-sm space-y-1">
                <p className="font-medium text-foreground">Préparation en cours…</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Si vous quittez cette page, la notification sera enregistrée dans vos
                  brouillons dès l'import terminé.
                </p>
              </div>
            </div>
          ) : null}
          {hint ? <p className="text-xs text-muted-foreground mb-2">{hint}</p> : null}
          {canManageHubup && groupMetaLoaded && !loading ? (
            (!joinWelcomeConfigured ||
              !hasUsableTopicsForCommunityPublish) ? (
              <div className="mb-6 rounded-lg border border-amber-600/40 bg-amber-500/10 dark:bg-amber-950/40 px-4 py-3 text-sm text-foreground space-y-2">
                {!joinWelcomeConfigured ? (
                  <p>
                    <span className="font-medium">Message de bienvenue requis.</span>{' '}
                    Configurez le titre et le texte envoyés aux nouveaux membres
                    depuis{' '}
                    <Link
                      to={`/hubup/g/${groupId}/subjects`}
                      className="text-primary font-medium underline underline-offset-2"
                    >
                      Mes rubriques
                    </Link>
                    {' — '}sans cela, les publications sur cette communauté sont bloquées.
                  </p>
                ) : null}
                {!hasUsableTopicsForCommunityPublish ? (
                  <p>
                    <span className="font-medium">Aucune rubrique dans cette communauté.</span>{' '}
                    {isAdmin ? (
                      <>
                        Créez au moins une rubrique dans{' '}
                        <Link
                          to={`/hubup/g/${groupId}/subjects`}
                          className="text-primary font-medium underline underline-offset-2"
                        >
                          Mes rubriques
                        </Link>
                        {' pour cibler cette communauté, ou utilisez « Notifier tout le monde ».'}
                      </>
                    ) : (
                      <>
                        Créez au moins une rubrique dans{' '}
                        <Link
                          to={`/hubup/g/${groupId}/subjects`}
                          className="text-primary font-medium underline underline-offset-2"
                        >
                          Mes rubriques
                        </Link>
                        {' avant de publier des notifications.'}
                      </>
                    )}
                  </p>
                ) : null}
              </div>
            ) : null
          ) : null}
        </div>

        {canManageHubup ? (
          <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden mb-8">
            <div className="p-4 md:p-6 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground mb-1">Brouillons</h2>
              <p className="text-xs text-muted-foreground">
                Enregistrez une annonce, modifiez-la, planifiez-la ou publiez-la depuis ce tableau.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                      Mis à jour
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[9rem]">
                      Rubrique
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[10rem]">
                      Titre
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider w-[120px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {drafts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-center text-sm text-muted-foreground"
                      >
                        Aucun brouillon pour cette communauté.
                      </td>
                    </tr>
                  ) : (
                    drafts.map((d) => (
                      <tr key={d._id} className="hover:bg-muted/40 transition-smooth">
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap align-top">
                          {formatParisDate(d.updatedAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground align-top">
                          {d.topicId === SUBJECT_NOTIFY_EVERYONE
                            ? 'Toutes les rubriques'
                            : topics.find((x) => x.id === d.topicId)?.title || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">
                              {d.title || '(Sans titre)'}
                            </span>
                            {d.aiGenerated ? (
                              <span
                                className="inline-flex items-center rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300"
                                title="Généré automatiquement à partir d’une page web"
                              >
                                IA
                              </span>
                            ) : null}
                          </div>
                          {d.scheduleSend ? (
                            <span className="block text-[10px] font-normal text-muted-foreground mt-0.5">
                              Planifié · {d.scheduleDate} {d.scheduleTime}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right align-top">
                          <div className="inline-flex flex-wrap justify-end gap-1">
                            <button
                              type="button"
                              className="p-1.5 rounded hover:bg-muted transition-smooth"
                              title="Modifier"
                              onClick={() => openDraft(d)}
                            >
                              <Icon name="Pencil" size={16} className="text-muted-foreground" />
                            </button>
                            <button
                              type="button"
                              className="p-1.5 rounded hover:bg-muted transition-smooth disabled:opacity-40"
                              title={
                                quotaExhausted && d.private !== true
                                  ? 'Quota épuisé'
                                  : 'Publier'
                              }
                              disabled={
                                saving ||
                                (quotaExhausted && d.private !== true)
                              }
                              onClick={() => void handlePublishDraftFromTable(d._id)}
                            >
                              <Icon name="Send" size={16} className="text-muted-foreground" />
                            </button>
                            <button
                              type="button"
                              className="p-1.5 rounded hover:bg-muted transition-smooth"
                              title="Supprimer"
                              onClick={() => void handleDeleteDraftRow(d._id)}
                            >
                              <Icon name="Trash2" size={16} className="text-error" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {canManageHubup ? (
          <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden mb-8">
            <div className="p-4 md:p-6 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Planifications en attente
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                      Envoi prévu
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[8rem]">
                      Cible
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[10rem]">
                      Titre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[12rem]">
                      Message
                    </th>
                    {canManageHubup ? (
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={scheduledColSpan}
                        className="px-4 py-6 text-center text-sm text-muted-foreground"
                      >
                        Chargement…
                      </td>
                    </tr>
                  ) : scheduledItemsListed.length === 0 ? (
                    <tr>
                      <td
                        colSpan={scheduledColSpan}
                        className="px-4 py-6 text-center text-sm text-muted-foreground"
                      >
                        Aucune planification en attente pour cette communauté.
                      </td>
                    </tr>
                  ) : (
                    scheduledItemsListed.map((s) => {
                      const plain = stripHtml(s.bodyHtml || s.body || '');
                      const short =
                        plain.length > BODY_PREVIEW_LEN
                          ? `${plain.slice(0, BODY_PREVIEW_LEN)}…`
                          : plain;
                      const canEditRow = !s.allTopics || isAdmin;
                      const canDeleteRow = !s.allTopics || isAdmin;
                      return (
                        <tr key={s._id} className="hover:bg-muted/50 transition-smooth">
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap align-top">
                            {s.scheduledForParis || formatParisDate(s.scheduledFor)}
                          </td>
                          <td className="px-4 py-3 text-sm align-top">
                            <div className="flex flex-col gap-1">
                              {s.allTopics ? (
                                <span className="text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full w-fit">
                                  Toutes les rubriques
                                </span>
                              ) : null}
                              <span className="text-foreground">{scheduleRowTargetLabel(s)}</span>
                              {s.notifyPush === false ? (
                                <span className="text-[10px] text-muted-foreground w-fit">
                                  Sans notification
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm align-top">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-foreground">{s.title}</span>
                              {s.priority === 'high' ? (
                                <span className="text-[10px] font-semibold uppercase text-error bg-error/10 px-2 py-0.5 rounded-full">
                                  Haute
                                </span>
                              ) : null}
                              {s.form?.name ? (
                                <span className="text-[10px] font-semibold uppercase text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                                  Formulaire
                                </span>
                              ) : null}
                              <HubupOneToOneBadge row={s} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground max-w-md align-top">
                            {short || '—'}
                          </td>
                          {canManageHubup ? (
                            <td className="px-4 py-3 text-right align-top">
                              <div className="inline-flex flex-wrap justify-end gap-1">
                                <button
                                  type="button"
                                  className="p-1.5 rounded hover:bg-muted transition-smooth disabled:opacity-40"
                                  title={
                                    canEditRow
                                      ? 'Modifier'
                                      : 'Réservé aux administrateurs'
                                  }
                                  aria-label="Modifier la planification"
                                  disabled={!canEditRow}
                                  onClick={() => canEditRow && openEditSchedule(s)}
                                >
                                  <Icon
                                    name="Pencil"
                                    size={16}
                                    className="text-muted-foreground"
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="p-1.5 rounded hover:bg-muted transition-smooth disabled:opacity-40"
                                  title={
                                    canDeleteRow
                                      ? 'Annuler l’envoi planifié'
                                      : 'Réservé aux administrateurs'
                                  }
                                  aria-label="Annuler la planification"
                                  disabled={!canDeleteRow}
                                  onClick={() => canDeleteRow && void handleDeleteSchedule(s)}
                                >
                                  <Icon
                                    name="Trash2"
                                    size={16}
                                    className="text-error"
                                  />
                                </button>
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Notifications
              </h2>
              <p className="text-xs text-muted-foreground">
                {messagesListed.length}{' '}
                {messagesListed.length === 1 ? 'notification' : 'notifications'}
              </p>
            </div>
            {canManageHubup ? (
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={openGenerateModal}
                  iconName="Globe"
                  iconPosition="left"
                  size="lg"
                  className="w-full sm:w-auto"
                  disabled={generateLoading}
                  title="Créer un brouillon depuis une page web"
                >
                  Générer une notification
                </Button>
              <Button
                onClick={openCreate}
                iconName="Plus"
                iconPosition="left"
                size="lg"
                  className="w-full sm:w-auto"
                title={
                    quotaExhausted
                      ? 'Quota épuisé : cochez « Annonce privée » à la publication'
                      : 'Créer une notification'
                  }
                  disabled={
                    !isAdmin &&
                    (!joinWelcomeConfigured || topicsSelectable.length === 0)
                }
              >
                Nouvelle notification
              </Button>
              </div>
            ) : null}
          </div>
          {canManageHubup && quotaExhausted ? (
            <div className="px-4 py-3 text-xs text-muted-foreground border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>Cette communauté n&apos;a plus de notifications disponibles.</span>
              {canBuyPacks ? (
                <Button type="button" size="xs" variant="outline" onClick={openOffersModal}>
                  Voir les offres
                </Button>
              ) : null}
            </div>
          ) : null}
          {canManageHubup &&
          !loading &&
          (isAdmin ? topics.length === 0 : topicsSelectable.length === 0) ? (
            <p className="px-4 py-3 text-xs text-muted-foreground border-b border-border">
              {isAdmin ? (
                <>
                  Aucune rubrique locale : vous pouvez quand même publier via « Notifier tout le monde » (toutes
                  les rubriques HubUP). Pour cibler uniquement cette communauté, créez des rubriques dans « Mes rubriques ».
                </>
              ) : (
                <>
                  Aucune rubrique dans cette communauté. Créez des rubriques dans « Mes rubriques » pour pouvoir
                  publier des notifications.
                </>
              )}
            </p>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Rubrique
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[10rem]">
                    Titre
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[14rem]">
                    Message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-[72px]">
                    Illustrations
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap w-[4.5rem]">
                    J&apos;aime
                  </th>
                  {canManageHubup ? (
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className="px-4 py-6 text-center text-sm text-muted-foreground"
                    >
                      Chargement…
                    </td>
                  </tr>
                ) : messagesListed.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className="px-4 py-6 text-center text-sm text-muted-foreground"
                    >
                      Aucune notification pour cette communauté.
                    </td>
                  </tr>
                ) : (
                  messagesListed.map((m) => {
                    const htmlRaw = (m.bodyHtml || '').trim();
                    const displayPlain = messagePlainPreview(m);
                    const isLong = displayPlain.length > BODY_PREVIEW_LEN;
                    const expanded = expandedBody.has(m.id);
                    const thumbPaths = messageImageUrlsFromModel(m);

                    return (
                      <tr key={m.id} className="hover:bg-muted/50 transition-smooth">
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap align-top">
                          {formatParisDate(m.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground align-top">
                          {m.topicTitle || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{m.title}</span>
                            {m.priority === 'high' ? (
                              <span className="text-[10px] font-semibold uppercase text-error bg-error/10 px-2 py-0.5 rounded-full">
                                Haute
                              </span>
                            ) : null}
                            {m.private === true ? (
                              <span
                                className="text-[10px] font-semibold uppercase text-red-700 bg-red-100 dark:bg-red-950/40 dark:text-red-300 px-2 py-0.5 rounded-full"
                                title="Hors fil HubUP — partage par lien direct"
                              >
                                Privée
                              </span>
                            ) : null}
                            <HubupOneToOneBadge row={m} />
                          </div>
                          {m.linkUrl ? (
                            <a
                              href={m.linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline"
                            >
                              <Icon name="ExternalLink" size={12} />
                              Lien
                            </a>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 max-w-md align-top">
                          {!displayPlain && !htmlRaw ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : expanded ? (
                            <div>
                              {htmlRaw ? (
                                <div
                                  className="prose prose-sm max-w-none text-foreground dark:prose-invert [&_p]:my-1 [&_ul]:my-1 [&_a]:text-primary"
                                  dangerouslySetInnerHTML={{ __html: htmlRaw }}
                                />
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">{displayPlain}</p>
                              )}
                              {isLong ? (
                                <button
                                  type="button"
                                  className="text-xs text-primary hover:underline mt-2"
                                  onClick={() => toggleBody(m.id)}
                                >
                                  Voir moins
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm text-muted-foreground">
                                {isLong
                                  ? `${displayPlain.slice(0, BODY_PREVIEW_LEN)}…`
                                  : displayPlain}
                              </p>
                              {isLong ? (
                                <button
                                  type="button"
                                  className="text-xs text-primary hover:underline mt-1"
                                  onClick={() => toggleBody(m.id)}
                                >
                                  Voir plus
                                </button>
                              ) : null}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {thumbPaths.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[120px]">
                              {thumbPaths.slice(0, 3).map((path, i) => {
                                const src = resolveHubupAssetUrl(path);
                                if (!src) return null;
                                return (
                                <a
                                  key={`${m.id}-img-${i}`}
                                  href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                                  className="block w-10 h-10 rounded-md border border-border overflow-hidden bg-muted shrink-0"
                                >
                                  <NotifIllustrationPreview
                                    src={src}
                                    path={path}
                                className="w-full h-full object-cover"
                              />
                            </a>
                                );
                              })}
                              {thumbPaths.length > 3 ? (
                                <span className="text-[10px] text-muted-foreground self-center">
                                  +{thumbPaths.length - 3}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <span
                            className="inline-flex items-center gap-1 text-sm text-muted-foreground tabular-nums"
                            title="J'aime reçus"
                          >
                            <Icon name="Heart" size={14} className="text-rose-500/80 shrink-0" />
                            {Number(m.likedCount ?? 0) || 0}
                          </span>
                        </td>
                        {canManageHubup ? (
                          <td className="px-4 py-3 text-right align-top">
                            <div className="inline-flex flex-wrap justify-end gap-1">
                              {m.formId ? (
                                <Link
                                  to={`/hubup/g/${groupId}/notifications/${m.id}/form-responses`}
                                  state={{ notificationTitle: m.title }}
                                  className="p-1.5 rounded hover:bg-muted transition-smooth inline-flex"
                                  title="Réponses du formulaire"
                                >
                                  <Icon
                                    name="ClipboardList"
                                    size={16}
                                    className="text-muted-foreground"
                                  />
                                </Link>
                              ) : null}
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Partager sur les réseaux sociaux"
                                aria-label="Partager sur les réseaux sociaux"
                                onClick={() => setSocialShareMessage(m)}
                              >
                                <Icon
                                  name="Share2"
                                  size={16}
                                  className="text-muted-foreground"
                                />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Lien direct dans l’app"
                                aria-label="Lien direct dans l’app"
                                onClick={() => openPublicLinkModal(m)}
                              >
                                <Icon
                                  name="Link2"
                                  size={16}
                                  className="text-muted-foreground"
                                />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Dupliquer"
                                onClick={() => openDuplicateFromMessage(m)}
                              >
                                <Icon
                                  name="Copy"
                                  size={16}
                                  className="text-muted-foreground"
                                />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Modifier"
                                onClick={() => openEdit(m)}
                              >
                                <Icon
                                  name="Pencil"
                                  size={16}
                                  className="text-muted-foreground"
                                />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth disabled:opacity-50"
                                title={
                                  quotaExhausted
                                    ? 'Quota épuisé'
                                    : m.private === true
                                      ? 'Rendre publique et envoyer le push'
                                      : m.notifyPush === false
                                        ? 'Push désactivé'
                                        : 'Renvoyer le push'
                                }
                                disabled={
                                  resendId === m.id ||
                                  (m.private !== true && m.notifyPush === false) ||
                                  quotaExhausted
                                }
                                onClick={() => void handleResend(m)}
                              >
                                <Icon
                                  name="Send"
                                  size={16}
                                  className="text-muted-foreground"
                                />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Supprimer"
                                aria-label="Supprimer"
                                onClick={() => handleDelete(m)}
                              >
                                <Icon
                                  name="Trash2"
                                  size={16}
                                  className="text-error"
                                />
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showForm ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-6xl max-h-[92vh] border border-border my-auto flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
              <h2 className="text-sm font-semibold text-foreground">
                {formPanel === 'form'
                  ? 'Formulaire de réponse'
                  : formPanel === 'event'
                    ? 'Événement calendrier'
                    : editingSchedule
                    ? 'Modifier la planification'
                    : editing
                      ? 'Modifier la notification'
                      : editingDraftId
                        ? 'Brouillon'
                        : isDuplicateDraft
                          ? 'Dupliquer la notification'
                          : 'Nouvelle notification'}
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth"
                onClick={closeForm}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
            {formPanel === 'form' ? (
              <div className="px-4 py-4">
                {formError ? (
                  <div
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive mb-4"
                    role="alert"
                  >
                    {formError}
                  </div>
                ) : null}
                <NotificationFormEditor
                  value={linkedForm || emptyNotificationForm()}
                  onChange={(draft) => {
                    setLinkedForm(draft);
                    if (formValidated) setFormValidated(false);
                  }}
                  onBack={() => {
                    setFormPanel('notification');
                    setFormError(null);
                  }}
                  onRemove={removeLinkedForm}
                  disabled={saving || savingDraft}
                  showRemove={formEnabled || hadFormInitially}
                />
                <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFormPanel('notification');
                      setFormError(null);
                    }}
                    disabled={saving || savingDraft}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    variant="success"
                    iconName="Check"
                    iconPosition="left"
                    onClick={finishFormEditor}
                    disabled={saving || savingDraft}
                  >
                    Valider le formulaire
                  </Button>
                </div>
              </div>
            ) : formPanel === 'event' ? (
              <div className="px-4 py-4">
                {formError ? (
                  <div
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive mb-4"
                    role="alert"
                  >
                    {formError}
                  </div>
                ) : null}
                <NotificationCalendarEventEditor
                  value={
                    linkedEvent || {
                      ...emptyNotificationCalendarEvent(),
                      startDate: minCalendarDateInputParis(),
                    }
                  }
                  onChange={(draft) => {
                    setLinkedEvent(draft);
                    if (eventValidated) setEventValidated(false);
                  }}
                  onBack={() => {
                    setFormPanel('notification');
                    setFormError(null);
                  }}
                  onRemove={removeLinkedEvent}
                  disabled={saving || savingDraft}
                  showRemove={eventEnabled || hadEventInitially}
                />
                <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFormPanel('notification');
                      setFormError(null);
                    }}
                    disabled={saving || savingDraft}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    variant="success"
                    iconName="Check"
                    iconPosition="left"
                    onClick={finishEventEditor}
                    disabled={saving || savingDraft}
                  >
                    Valider l’événement
                  </Button>
                </div>
              </div>
            ) : (
              <>
            <div className="px-4 pt-4">
              {draftAiMediaReview ? (
                <div className="rounded-lg border border-primary/35 bg-primary/5 p-3 mb-4">
                  <HubupAiSuggestedMediaReview
                    compact
                    items={draftAiMediaReview.items}
                    loading={generateMediaImportLoading}
                    error={generateMediaImportError}
                    onConfirm={(urls) => void finishDraftAiMediaReview(urls)}
                    onSkip={() => void skipDraftAiMediaReview()}
                  />
                </div>
              ) : null}
              <div className="rounded-lg border-2 border-dashed border-primary/35 bg-primary/5 p-3 space-y-2 scroll-mt-2">
                <label className="text-xs font-semibold text-foreground mb-0 block">
                  Illustrations (optionnel, max {NOTIF_IMAGES_MAX} fichiers) — images
                  PNG/JPEG/WebP/GIF jusqu&apos;à 3 Mo ; vidéos MP4, WebM, MOV ou PDF
                  jusqu&apos;à 50 Mo
                </label>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  <input
                    id="hubup-notif-images-input"
                    type="file"
                    multiple
                    accept={NOTIF_MEDIA_ACCEPT}
                    className="block w-full min-w-0 text-xs text-foreground file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-foreground hover:file:bg-muted"
                    disabled={
                      saving ||
                      (form.imageUrls?.length ?? 0) + pendingImageItems.length >=
                        NOTIF_IMAGES_MAX
                    }
                    onChange={handleImageFile}
                  />
                </div>
                {pendingImageItems.length > 0 ? (
                  <div
                    className="mt-1 rounded-md border border-border bg-card px-2 py-2 shadow-sm"
                    aria-live="polite"
                  >
                    <p className="text-[10px] font-semibold text-primary uppercase tracking-wide mb-2">
                      En attente (envoi à l’enregistrement)
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {pendingImageItems.map((p) => (
                        <div
                          key={p.id}
                          className="flex flex-col gap-1 shrink-0 w-28"
                        >
                          <div className="relative w-28 h-28 rounded-md border-2 border-dashed border-primary/50 overflow-hidden bg-muted">
                            <NotifIllustrationPreview
                              src={p.objectUrl}
                              file={p.file}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              className="absolute top-0.5 right-0.5 p-0.5 rounded bg-background/90 text-error text-[10px] font-medium opacity-90 hover:opacity-100"
                              title="Retirer"
                              onClick={() => removePendingImageItem(p.id)}
                            >
                              ×
                            </button>
                          </div>
                          <span
                            className="text-[10px] text-foreground leading-tight line-clamp-2 break-all font-medium"
                            title={p.file.name}
                          >
                            {p.file.name}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="text-xs text-destructive hover:underline mt-2"
                      onClick={() => clearPendingImages()}
                    >
                      Retirer tous les fichiers en attente
                    </button>
                  </div>
                ) : null}
                {form.imageUrls.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {form.imageUrls.map((path) => {
                      const src = resolveHubupAssetUrl(path);
                      if (!src) return null;
                      const isPdf = isNotifPdfPath(path);
                      return (
                        <div
                          key={path}
                          className={`relative shrink-0 ${isPdf ? 'w-28' : 'w-24 h-24'}`}
                        >
                          <div
                            className={`relative rounded-md border-2 border-border overflow-hidden group ${
                              isPdf ? 'w-28 h-28' : 'w-24 h-24'
                            }`}
                          >
                            {isPdf ? (
                              <a
                                href={src}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full h-full"
                                title={notifPdfDisplayName(path)}
                              >
                                <NotifIllustrationPreview
                                  src={src}
                                  path={path}
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ) : (
                              <NotifIllustrationPreview
                                src={src}
                                path={path}
                                className="w-full h-full object-cover"
                              />
                            )}
                            <button
                              type="button"
                              className="absolute top-0.5 right-0.5 p-0.5 rounded bg-background/90 text-error text-[10px] font-medium opacity-90 hover:opacity-100"
                              title="Retirer"
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  imageUrls: f.imageUrls.filter((u) => u !== path),
                                }))
                              }
                            >
                              ×
                            </button>
                          </div>
                          {isPdf ? (
                            <span
                              className="block text-[10px] text-foreground leading-tight line-clamp-2 break-all font-medium mt-1"
                              title={notifPdfDisplayName(path)}
                            >
                              {notifPdfDisplayName(path)}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                {form.imageUrls.length > 0 ? (
                  <button
                    type="button"
                    className="text-xs text-destructive hover:underline"
                    onClick={() => setForm((f) => ({ ...f, imageUrls: [] }))}
                  >
                    Tout retirer (fichiers déjà enregistrés)
                  </button>
                ) : null}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="px-4 pb-4 pt-2 space-y-4">
              {loadingLinkedForm || loadingLinkedEvent ? (
                <p className="text-xs text-muted-foreground">
                  {loadingLinkedForm && loadingLinkedEvent
                    ? 'Chargement du formulaire et de l’événement…'
                    : loadingLinkedForm
                      ? 'Chargement du formulaire…'
                      : 'Chargement de l’événement…'}
                </p>
              ) : null}
              {formError ? (
                <div
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  role="alert"
                >
                  {formError}
                </div>
              ) : null}
              <div className="rounded-lg border border-border bg-muted/20 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    Formulaire de réponse
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formEnabled && formValidated && linkedForm?.name?.trim()
                      ? `« ${linkedForm.name.trim()} » — ${linkedForm.questions?.length || 0} champ(s)`
                      : formEnabled && linkedForm && !formValidated
                        ? 'Brouillon non validé — cliquez sur « Valider le formulaire ».'
                        : 'Optionnel — les visiteurs pourront y répondre depuis le fil d’annonces.'}
                  </p>
                </div>
                <Button
                  type="button"
                  variant={formEnabled ? 'outline' : 'default'}
                  size="sm"
                  iconName={formEnabled ? 'Pencil' : 'FileInput'}
                  iconPosition="left"
                  onClick={openFormEditor}
                  disabled={saving || savingDraft || loadingLinkedForm}
                >
                  {formEnabled ? 'Modifier le formulaire' : 'Ajouter un formulaire'}
                </Button>
              </div>
              <div className="rounded-lg border border-border bg-muted/20 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    Événement calendrier
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {eventEnabled && eventValidated && linkedEvent?.eventTitle?.trim()
                      ? formatCalendarEventPreviewLine(linkedEvent) ||
                        `« ${linkedEvent.eventTitle.trim()} »`
                      : eventEnabled && linkedEvent && !eventValidated
                        ? 'Brouillon non validé — cliquez sur « Valider l’événement ».'
                        : 'Optionnel — les visiteurs pourront l’ajouter à leur calendrier.'}
                  </p>
                </div>
                <Button
                  type="button"
                  variant={eventEnabled ? 'outline' : 'default'}
                  size="sm"
                  iconName={eventEnabled ? 'Pencil' : 'Calendar'}
                  iconPosition="left"
                  onClick={openEventEditor}
                  disabled={saving || savingDraft || loadingLinkedEvent}
                >
                  {eventEnabled ? 'Modifier l’événement' : 'Ajouter un événement'}
                </Button>
              </div>
              {!editing ? (
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Rubrique
                  </label>
                  <select
                    required
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={form.topicId}
                    onChange={(ev) =>
                      setForm((f) => ({ ...f, topicId: ev.target.value }))
                    }
                  >
                    {isAdmin ? (
                      <option value={SUBJECT_NOTIFY_EVERYONE}>
                        Notifier tout le monde
                      </option>
                    ) : null}
                    {topicsSelectable.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                  {!isAdmin && !editing ? (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Seules les rubriques listées ci-dessus (cette communauté) sont disponibles.
                    </p>
                  ) : null}
                  {isAdmin && form.topicId === SUBJECT_NOTIFY_EVERYONE ? (
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5">
                      Diffusion sur toutes les rubriques de toutes les communautés HubUP (abonnés concernés)
                    </p>
                  ) : null}
                  {editingSchedule &&
                  !editingSchedule.allTopics &&
                  editingSchedule.topicIds?.length > 1 ? (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Plusieurs rubriques étaient ciblées. Le menu ci-dessus pré-sélectionne la première ; à
                      l’enregistrement, la cible devient cette rubrique (ou « Notifier tout le monde » si vous
                      choisissez cette option en tant qu’admin).
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Titre
                </label>
                <input
                  type="text"
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={form.title}
                  onChange={(ev) =>
                    setForm((f) => ({ ...f, title: ev.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Message
                </label>
                <RichTextEditor
                    value={form.bodyHtml}
                    onChange={(v) => setForm((f) => ({ ...f, bodyHtml: v }))}
                  enableImages
                  onImageUpload={handleBodyInlineImageUpload}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Optionnel si un formulaire validé ou des illustrations sont
                  présents (titre obligatoire). Images inline : bouton image dans
                  la barre d’outils, PNG/JPEG/WebP/GIF max 3 Mo.
                </p>
              </div>
              <div>
                <label
                  className="text-xs font-medium text-foreground mb-1 block"
                  title="Bouton d’action dans l’annonce (public ou privée)"
                >
                  Action
                </label>
                <input
                  type="url"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={form.linkUrl}
                  onChange={(ev) =>
                    setForm((f) => ({ ...f, linkUrl: ev.target.value }))
                  }
                  placeholder="https://…"
                  title="URL ouverte depuis l’annonce"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Priorité
                </label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={form.priority}
                  onChange={(ev) =>
                    setForm((f) => ({ ...f, priority: ev.target.value }))
                  }
                >
                  <option value="normal">Normale</option>
                  <option value="high">Haute</option>
                </select>
              </div>
              {showAnnuaireRecipientPicker ? (
                <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Destinataires
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Par défaut, toute la rubrique reçoit l’annonce. Vous pouvez
                      limiter aux personnes de votre annuaire formulaires.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={limitToAnnuaire}
                      onChange={(ev) => {
                        const on = ev.target.checked;
                        setLimitToAnnuaire(on);
                        if (!on) {
                          setTargetVisitorIds([]);
                        } else {
                          setRecipientModalOpen(true);
                        }
                      }}
                      className="rounded border-input shrink-0"
                    />
                    <span>Envoyer uniquement à certaines personnes</span>
                  </label>
                  {limitToAnnuaire ? (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <p className="text-sm text-foreground flex-1">
                        {targetVisitorIds.length === 0
                          ? 'Aucune personne sélectionnée.'
                          : `${targetVisitorIds.length} personne${targetVisitorIds.length !== 1 ? 's' : ''} sélectionnée${targetVisitorIds.length !== 1 ? 's' : ''}.`}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        iconName="Users"
                        iconPosition="left"
                        onClick={() => setRecipientModalOpen(true)}
                      >
                        Choisir…
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <label
                className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
                title="Hors fil et sans push — action et lien direct possibles"
              >
                <input
                  type="checkbox"
                  checked={isPrivateNotification}
                  onChange={(ev) => {
                    const on = ev.target.checked;
                    setIsPrivateNotification(on);
                    if (on) {
                      setNotifyPush(false);
                      resetAnnuaireRecipientState();
                    }
                  }}
                  className="rounded border-input shrink-0"
                />
                <span>Annonce privée</span>
              </label>
              {isAdmin && !editing && !isPrivateNotification ? (
                <label
                  className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
                  title="Push mobile à la publication"
                >
                  <input
                    type="checkbox"
                    checked={notifyPush}
                    onChange={(ev) => setNotifyPush(ev.target.checked)}
                    className="rounded border-input shrink-0"
                  />
                  <span>Notifier les utilisateurs</span>
                </label>
              ) : null}
              {editing ? (
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editReschedule}
                      onChange={(ev) => {
                        const on = ev.target.checked;
                        setEditReschedule(on);
                        if (on && !scheduleDate.trim()) {
                          setScheduleDate(minScheduleDateInputParis());
                        }
                      }}
                      className="rounded border-input"
                    />
                    <span>
                      Re-planifier l’envoi{' '}
                      <span className="text-muted-foreground font-normal">
                        (même si la notification est déjà passée — date et heure Europe/Paris)
                      </span>
                    </span>
                  </label>
                  {editReschedule ? (
                    <div className="flex flex-wrap gap-3 items-end pt-1">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                          Date
                </label>
                  <input
                          type="date"
                          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={scheduleDate}
                          min={minScheduleDateInputParis()}
                          onChange={(ev) => setScheduleDate(ev.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">
                          Heure
                        </label>
                        <input
                          type="time"
                          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={scheduleTime}
                          onChange={(ev) => setScheduleTime(ev.target.value)}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {!editing && !editingSchedule ? (
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scheduleSend}
                      onChange={(ev) => {
                        const on = ev.target.checked;
                        setScheduleSend(on);
                        if (on && !scheduleDate) {
                          setScheduleDate(minScheduleDateInputParis());
                        }
                      }}
                      className="rounded border-input"
                    />
                    <span>
                      Planifier l’envoi{' '}
                      <span className="text-muted-foreground font-normal">
                        (date et heure en fuseau Europe/Paris)
                      </span>
                    </span>
                  </label>
                  {scheduleSend ? (
                    <div className="flex flex-wrap gap-3 items-end pt-1">
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">
                          Date
                        </label>
                        <input
                          type="date"
                          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={scheduleDate}
                          min={minScheduleDateInputParis()}
                          onChange={(ev) => setScheduleDate(ev.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">
                          Heure
                        </label>
                        <input
                          type="time"
                          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={scheduleTime}
                          onChange={(ev) => setScheduleTime(ev.target.value)}
                        />
                      </div>
                  </div>
                ) : null}
                </div>
                ) : null}
              {editingSchedule ? (
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                  <p className="text-xs font-medium text-foreground">
                    Date et heure d’envoi <span className="font-normal text-muted-foreground">(Europe/Paris)</span>
                  </p>
                  <div className="flex flex-wrap gap-3 items-end pt-1">
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">
                        Date
                      </label>
                      <input
                        type="date"
                        className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        value={scheduleDate}
                        min={minScheduleDateInputParis()}
                        onChange={(ev) => setScheduleDate(ev.target.value)}
                      />
              </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">
                        Heure
                      </label>
                      <input
                        type="time"
                        className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        value={scheduleTime}
                        onChange={(ev) => setScheduleTime(ev.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeForm}>
                  Annuler
                </Button>
                {!editing && !editingSchedule ? (
                  <Button
                    type="button"
                    variant="outline"
                    iconName="FileText"
                    iconPosition="left"
                    disabled={saving || savingDraft}
                    onClick={() => void handleSaveDraft()}
                  >
                    {savingDraft ? 'Enregistrement…' : 'Enregistrer le brouillon'}
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="success"
                  iconName="Save"
                  iconPosition="left"
                  disabled={saving}
                >
                  {saving
                    ? editingSchedule
                      ? 'Enregistrement…'
                      : scheduleSend && !editing
                        ? 'Planification…'
                        : editing && editReschedule
                          ? 'Replanification…'
                          : 'Publication…'
                    : editingSchedule
                      ? 'Enregistrer la planification'
                      : editing
                        ? editReschedule
                          ? 'Enregistrer et replanifier'
                          : 'Enregistrer'
                        : scheduleSend
                          ? 'Planifier l’envoi'
                          : 'Publier'}
                </Button>
              </div>
            </form>
              </>
            )}
              </div>
              <HubupNotificationLivePreviewAside
                mode={
                  formPanel === 'form'
                    ? 'form'
                    : formPanel === 'event'
                      ? 'event'
                      : 'notification'
                }
                preview={notificationPreviewCard}
                form={linkedForm}
                calendarEvent={
                  linkedEvent || {
                    ...emptyNotificationCalendarEvent(),
                    startDate: minCalendarDateInputParis(),
                  }
                }
              />
            </div>
          </div>
        </div>
      ) : null}

      {socialShareMessage ? (
        <HubupNotificationSocialShareModal
          message={socialShareMessage}
          onClose={() => setSocialShareMessage(null)}
        />
      ) : null}

      {publicLinkModal ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1200] p-4">
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-sm border border-border"
            role="dialog"
            aria-labelledby="hubup-public-link-title"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2
                id="hubup-public-link-title"
                className="text-sm font-semibold text-foreground pr-2"
              >
                Lien public
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth shrink-0"
                aria-label="Fermer"
                onClick={() => setPublicLinkModal(null)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-muted-foreground line-clamp-3">
                {publicLinkModal.title}
              </p>
              <div className="flex justify-center bg-background rounded-lg p-4 border border-border">
                <QRCode value={publicLinkModal.url} size={180} level="M" />
              </div>
              <p className="text-xs text-foreground break-all leading-relaxed text-center">
                {publicLinkModal.url}
              </p>
              {publicLinkCopyHint ? (
                <p className="text-xs text-muted-foreground text-center">
                  {publicLinkCopyHint}
                </p>
              ) : null}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                iconName="Copy"
                iconPosition="left"
                onClick={() => void copyPublicLinkFromModal()}
              >
                Copier le lien
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <HubupFormAnnuaireRecipientModal
        open={recipientModalOpen}
        contacts={annuaireContacts}
        selectedIds={targetVisitorIds}
        onClose={() => setRecipientModalOpen(false)}
        onConfirm={(ids) => {
          setTargetVisitorIds(ids);
          setLimitToAnnuaire(ids.length > 0);
          setRecipientModalOpen(false);
        }}
      />
      <GenerateNotificationBrowserModal
        open={generateBrowserOpen}
        loading={generateLoading}
        error={generateError}
        onClose={closeGenerateModal}
        onSubmit={handleGenerateFromUrl}
        mediaReview={generateMediaReview}
        mediaReviewLoading={generateMediaImportLoading}
        mediaReviewError={generateMediaImportError}
        onMediaReviewConfirm={(urls) => void finishGenerateMediaReview(urls)}
        onMediaReviewSkip={() => void skipGenerateMediaReview()}
      />
    </>
  );
};

export default HubupNotifications;
